// We don't depend on @simplewebauthn/types directly; use the JSON shapes
// inferred from startRegistration/startAuthentication arguments via the
// browser package's type re-exports.
import type { startAuthentication, startRegistration } from '@simplewebauthn/browser';
type PublicKeyCredentialCreationOptionsJSON = Parameters<typeof startRegistration>[0]['optionsJSON'];
type PublicKeyCredentialRequestOptionsJSON = Parameters<typeof startAuthentication>[0]['optionsJSON'];

// Tiny fetch wrapper. Stores the JWT access token from /auth/login/finish
// and adds it as Authorization: Bearer on every API call.
//
// We keep `setStoredClaims` for backward compat with the SetupPage's local
// claim seed during bootstrap, but the production source of truth is the
// access_token + the cached user payload.

const TOKEN_KEY = 'ai-outlook:access-token';
const USER_KEY = 'ai-outlook:user';
const CLAIMS_KEY = 'ai-outlook:test-claims';
// Round 28-U: refresh-token family for silent access-token rotation.
const REFRESH_TOKEN_KEY = 'ai-outlook:refresh-token';
const REFRESH_FAMILY_KEY = 'ai-outlook:refresh-family';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  global_role: 'superadmin' | 'admin' | 'ops' | 'viewer' | 'none';
}

export interface Claims {
  sub: string;
  global_role: 'superadmin' | 'admin' | 'ops' | 'viewer' | 'none';
  accounts: Record<string, 'owner' | 'viewer'>;
  cnf: { jkt: string };
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setStoredToken(t: string | null): void {
  if (t === null) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, t);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}
export function setStoredUser(u: AuthUser | null): void {
  if (u === null) localStorage.removeItem(USER_KEY);
  else localStorage.setItem(USER_KEY, JSON.stringify(u));
}

/** Wipes all auth state — used on logout + on 401 from any API call. */
export function clearAllAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(CLAIMS_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_FAMILY_KEY);
}

// v3.6.2 followup (2026-06-20): the C# side has a 5-consecutive-401 trap
// that clears Config.AccessToken + pushes `auth_expired` to the WebView2.
// But the C# trap only fires when C# code makes API calls; the WebView2
// React layer makes its own fetches (via this api.ts) and has its own
// localStorage token. If C# happens to be making different calls that
// don't 401 while React-layer calls all 401, the C# counter never trips
// and React keeps spinning on stale-token errors forever.
//
// rabih's report: "STILL unauthenticated" on v3.6.2 because the JS retry
// loop in send()/sendText() just throws lastErr on outcome=='none' (COM
// addin, no refresh token) — never clears, never signals.
//
// Fix: track a module-scope counter here, mirror the C# 5-strike logic.
// When 5 React-layer 401s in a row hit with no refresh-token to use,
// wipe localStorage + dispatch a custom event the AddinPage listens for
// (which then setToken(null) → re-renders <AddinLogin>).
let _consecutive401s = 0;
function on401(): void {
  _consecutive401s += 1;
  if (_consecutive401s >= 5) {
    _consecutive401s = 0;
    try { clearAllAuth(); } catch { /* ignore */ }
    try {
      window.dispatchEvent(new CustomEvent('ai-outlook:auth-revoked', {
        detail: { reason: 'consecutive_401s' },
      }));
    } catch { /* CustomEvent not available — caller polls localStorage */ }
  }
}
function onAuthSuccess(): void {
  if (_consecutive401s !== 0) _consecutive401s = 0;
}

/** Best-effort JWK thumbprint of the dashboard's DPoP keypair. Returns null
 *  if WebCrypto/IndexedDB are unavailable so legacy paths still work. */
async function safeJkt(): Promise<string | null> {
  try {
    const { jwkThumbprint } = await import('./auth/dpop.js');
    return await jwkThumbprint();
  } catch {
    return null;
  }
}

/** Round 28-U: mints a refresh-token family. Call once after login lands. */
export async function issueRefreshToken(): Promise<void> {
  const r = await sendRaw<{ refresh_token: string; family_id: string }>(
    '/auth/token/issue',
    { method: 'POST' },
    { skipRefresh: true },
  );
  localStorage.setItem(REFRESH_TOKEN_KEY, r.refresh_token);
  localStorage.setItem(REFRESH_FAMILY_KEY, r.family_id);
}

/** Round 28-U: rotate refresh + re-mint access.
 *  Returns 'refreshed' on success, 'failed' if a refresh was attempted and
 *  rejected (replay/expired family — caller should wipe auth), or 'none' if
 *  no refresh token was stored to begin with (COM addin pushes only an
 *  access token; a transient 401 must NOT wipe its 30-day token). */
type RefreshOutcome = 'refreshed' | 'failed' | 'none';
async function tryRefreshAccess(): Promise<RefreshOutcome> {
  const refresh_token = localStorage.getItem(REFRESH_TOKEN_KEY);
  const family_id = localStorage.getItem(REFRESH_FAMILY_KEY);
  if (!refresh_token || !family_id) return 'none';
  try {
    const dpop_jkt = await safeJkt();
    const r = await sendRaw<{ access_token: string; refresh_token: string; family_id: string }>(
      '/auth/token/refresh',
      {
        method: 'POST',
        body: JSON.stringify({ family_id, refresh_token, ...(dpop_jkt ? { dpop_jkt } : {}) }),
      },
      { skipRefresh: true, skipAuth: true },
    );
    setStoredToken(r.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, r.refresh_token);
    localStorage.setItem(REFRESH_FAMILY_KEY, r.family_id);
    return 'refreshed';
  } catch {
    // Replay detection / expired family — drop everything; user re-signs in.
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_FAMILY_KEY);
    return 'failed';
  }
}

/** @deprecated Use getStoredUser + getStoredToken — kept for SetupPage. */
export function getStoredClaims(): Claims | null {
  const raw = localStorage.getItem(CLAIMS_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Claims;
  } catch {
    return null;
  }
}
/** @deprecated Use setStoredToken + setStoredUser. */
export function setStoredClaims(c: Claims | null): void {
  if (!c) localStorage.removeItem(CLAIMS_KEY);
  else localStorage.setItem(CLAIMS_KEY, JSON.stringify(c));
}

interface SendOpts {
  /** Skip the refresh-on-401 retry. Used by token endpoints themselves. */
  skipRefresh?: boolean;
  /** Skip Authorization header. Used by /auth/token/refresh which
   *  authenticates via the refresh token in the body, not the access token. */
  skipAuth?: boolean;
}

/** Shared: prepare headers + DPoP proof + execute fetch + map !ok → ApiError.
 *  Returns the Response so callers can decide whether to parse JSON or text. */
async function executeAuthed(url: string, init: RequestInit, opts: SendOpts): Promise<Response> {
  const headers: Record<string, string> = {
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body !== undefined && init.body !== null && !headers['content-type']) {
    headers['content-type'] = 'application/json';
  }
  // Hold the token in a local so we can pass the same value to both the
  // Authorization header and the DPoP `ath` claim. RFC 9449 §4.3 requires
  // ath = base64url(SHA-256(accessToken)) when both headers are present;
  // re-reading from storage between the two would let a refresh race blow
  // the binding.
  const bearerToken = !opts.skipAuth ? getStoredToken() : null;
  if (bearerToken) headers.authorization = `Bearer ${bearerToken}`;
  try {
    const { signDpopProof } = await import('./auth/dpop.js');
    const method = (init.method ?? 'GET').toUpperCase();
    headers.DPoP = await signDpopProof(
      method,
      url.startsWith('http') ? url : window.location.origin + url,
      bearerToken,
    );
  } catch {
    // proof gen failed — let the request proceed without DPoP
  }
  const r = await fetch(url, { ...init, headers });
  if (!r.ok) {
    let detail: unknown = null;
    try {
      detail = await r.json();
    } catch {
      // ignore
    }
    throw new ApiError(
      r.status,
      typeof detail === 'object' && detail && 'error' in detail
        ? String((detail as { error: string }).error)
        : r.statusText,
      detail,
    );
  }
  return r;
}

async function sendRaw<T>(url: string, init: RequestInit = {}, opts: SendOpts = {}): Promise<T> {
  const r = await executeAuthed(url, init, opts);
  return (await r.json()) as T;
}

/** Text-response variant of sendRaw. Same auth/DPoP plumbing; caller wants
 *  plain text (e.g. /addin/diagnose/me returns markdown). */
async function sendRawText(url: string, init: RequestInit = {}, opts: SendOpts = {}): Promise<string> {
  const r = await executeAuthed(url, init, opts);
  return await r.text();
}

async function send<T>(url: string, init: RequestInit = {}): Promise<T> {
  // Round 25 R25-4: DPoP proof attached in sendRaw. Round 28-U: 401 on a
  // user-facing API now triggers a silent refresh-token rotation + retry.
  // Round 29 hardening: 503/504/network errors auto-retry up to 2× with
  // exponential backoff. The brain occasionally returns 503 when both
  // upstreams are temporarily wedged under bulk-index load — without
  // this retry, every panel surface flashes an error and the user sees
  // a sea of red. With it, transient busyness is invisible: most calls
  // succeed within 1–2s of the first attempt's failure.
  //
  // Mutations (POST/PUT/PATCH/DELETE) only retry network errors, NOT
  // 503s — a 503 might mean the brain accepted-then-failed, retrying
  // could double-apply (e.g. snooze applied twice, mute_sender duplicated).
  // GETs are idempotent so 503-retry is always safe.
  const method = ((init.method ?? 'GET') as string).toUpperCase();
  const isGet = method === 'GET' || method === 'HEAD';
  const MAX_RETRIES = 2;
  let lastErr: unknown = null;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const result = await sendRaw<T>(url, init);
      onAuthSuccess(); // any 2xx resets the consecutive-401 counter
      return result;
    } catch (err) {
      lastErr = err;
      // Auth refresh path (one-shot, doesn't count as a retry attempt).
      // On 'refreshed' we retry once. On 'failed' (refresh attempted and
      // rejected) we wipe — replay/expired family means the user must
      // re-auth. On 'none' (no refresh token stored, e.g. COM addin which
      // pushes a 30-day access token via PostWebMessageAsJson and never
      // mints a refresh family) we used to silently propagate. v3.6.2
      // followup: instead, ALSO count consecutive 401s and, after 5,
      // clear local auth + dispatch ai-outlook:auth-revoked so the
      // AddinPage flips to the in-pane LoginForm without manual help.
      if (err instanceof ApiError && err.status === 401) {
        const outcome = await tryRefreshAccess();
        if (outcome === 'refreshed') {
          try {
            const r2 = await sendRaw<T>(url, init);
            onAuthSuccess();
            return r2;
          } catch (err2) { lastErr = err2; on401(); }
        } else if (outcome === 'failed') {
          clearAllAuth();
        } else {
          // outcome === 'none' — long-lived addin token. Count strikes.
          on401();
        }
        throw lastErr;
      }
      // Decide whether to retry. Conservative defaults — only auto-recover
      // from transient infra errors, not data errors (4xx).
      // AbortError is NOT transient: callers (SearchPanel keystroke debounce,
      // route teardown on unmount) deliberately abort via AbortController to
      // CANCEL a superseded request. Retrying here would resurrect a request
      // the caller explicitly killed, sending duplicate hits to the brain.
      const isTransient =
        (err instanceof ApiError && (err.status === 503 || err.status === 504)) ||
        (err instanceof TypeError); // fetch() rejects with TypeError on network failure
      if (!isTransient) throw err;
      if (init.signal?.aborted) throw err; // caller cancelled, don't retry
      if (!isGet && err instanceof ApiError) throw err; // mutations: bail on HTTP errors
      if (attempt >= MAX_RETRIES) throw err;
      // Exponential backoff with jitter: 400ms, 1200ms.
      const baseMs = 400 * Math.pow(3, attempt);
      const jitter = Math.random() * 200;
      await new Promise<void>((resolve) => setTimeout(resolve, baseMs + jitter));
    }
  }
  throw lastErr;
}

/** Text-response sibling of `send` — same DPoP/refresh/retry behavior,
 *  but resolves to the response body as a string instead of parsed JSON.
 *  Use for endpoints that return text/markdown / text/plain (e.g.
 *  /addin/diagnose/me). */
async function sendText(url: string, init: RequestInit = {}): Promise<string> {
  const method = ((init.method ?? 'GET') as string).toUpperCase();
  const isGet = method === 'GET' || method === 'HEAD';
  const MAX_RETRIES = 2;
  let lastErr: unknown = null;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const result = await sendRawText(url, init);
      onAuthSuccess();
      return result;
    } catch (err) {
      lastErr = err;
      if (err instanceof ApiError && err.status === 401) {
        const outcome = await tryRefreshAccess();
        if (outcome === 'refreshed') {
          try {
            const r2 = await sendRawText(url, init);
            onAuthSuccess();
            return r2;
          } catch (err2) { lastErr = err2; on401(); }
        } else if (outcome === 'failed') {
          clearAllAuth();
        } else {
          on401();
        }
        throw lastErr;
      }
      // Same AbortError handling as send() — don't resurrect cancelled requests.
      const isTransient =
        (err instanceof ApiError && (err.status === 503 || err.status === 504)) ||
        (err instanceof TypeError);
      if (!isTransient) throw err;
      if (init.signal?.aborted) throw err;
      if (!isGet && err instanceof ApiError) throw err;
      if (attempt >= MAX_RETRIES) throw err;
      const baseMs = 400 * Math.pow(3, attempt);
      const jitter = Math.random() * 200;
      await new Promise<void>((resolve) => setTimeout(resolve, baseMs + jitter));
    }
  }
  throw lastErr;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detail: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface AccountSummary {
  id: string;
  label: string;
  enabled: boolean;
  timezone: string;
  shadow_mode: boolean;
  imap: {
    host: string;
    port: number;
    secure: boolean;
    username: string;
    folders: { inbox: string; sent: string; drafts: string; processed_label: string };
    has_password: boolean;
  };
  digest: { enabled: boolean; time: string };
  alert_overrides: { vip_channels: string[]; important_channels: string[] };
  reply_rag_corpus: { include_external: boolean };
  created_at: string;
  updated_at: string;
}

export interface SenderPreference {
  account_id: string;
  sender_email: string;
  sender_domain: string;
  vip_score: number;
  is_blocked: boolean;
  is_newsletter: boolean;
  quiet_hours: boolean;
  notes: string | null;
}

export interface SenderPrefUpsert {
  vip_score?: number;
  is_blocked?: boolean;
  is_newsletter?: boolean;
  quiet_hours?: boolean;
  notes?: string | null;
}

// Phase 4: Action Inbox 2.0 types
export type ActionInboxState = 'act' | 'wait' | 'know' | 'automate' | 'ignore';
export interface ActionInboxItem {
  id: string;
  message_id: string;
  thread_id: string | null;
  subject: string | null;
  from_addr: string;
  to_addrs: string | null;
  received_at: string;
  classification_tier: string | null;
  folder: string | null;
  body_redacted: string | null;
  evidence: string[];
  state: ActionInboxState;
  // Round 28-AC: dedup grouping. The brain collapses (subject+sender+day)
  // duplicates server-side; this row stands for `duplicate_count` total
  // copies, with all sibling message_ids in `duplicate_message_ids`.
  duplicate_count?: number;
  duplicate_message_ids?: string[];
}

export interface ProbeResult {
  ok: boolean;
  reason?: string;
  message?: string;
  delimiter?: string;
  folders?: Array<{ path: string; name: string; special_use: string | null; selectable: boolean }>;
  inbox?: { uidvalidity: number; uidnext: number };
  suggested?: { sent: string | null; drafts: string | null };
}

export const api = {
  health: () => send<{ status: string }>('/health/live'),
  killswitchGet: () => send<{ flags: { ai_actions_enabled: boolean } }>('/admin/killswitch'),
  killswitchPost: (action: 'engage' | 'lift', reason: string) =>
    send<{ ok: boolean }>('/admin/killswitch', {
      method: 'POST',
      body: JSON.stringify({ action, reason }),
    }),

  accountsList: () => send<{ accounts: AccountSummary[] }>('/admin/accounts'),
  accountGet: (id: string) => send<AccountSummary>(`/admin/accounts/${encodeURIComponent(id)}`),
  accountCreate: (input: {
    label: string;
    timezone?: string;
    imap: { host: string; port?: number; secure?: boolean; username: string; password: string };
  }) =>
    send<AccountSummary>('/admin/accounts', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  accountPatch: (
    id: string,
    body: {
      label?: string;
      timezone?: string;
      enabled?: boolean;
      shadow_mode?: boolean;
      imap?: {
        host?: string;
        port?: number;
        secure?: boolean;
        username?: string;
        password?: string;
        folders?: AccountSummary['imap']['folders'];
      };
    },
  ) =>
    send<AccountSummary>(`/admin/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  accountDelete: (id: string) =>
    send<{ ok: boolean }>(`/admin/accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  accountEnable: (id: string) =>
    send<{ ok: boolean; account: AccountSummary }>(
      `/admin/accounts/${encodeURIComponent(id)}/enable`,
      { method: 'POST' },
    ),
  accountDisable: (id: string) =>
    send<{ ok: boolean; account: AccountSummary }>(
      `/admin/accounts/${encodeURIComponent(id)}/disable`,
      { method: 'POST' },
    ),
  accountProbe: (input: {
    host: string;
    port?: number;
    secure?: boolean;
    username: string;
    password: string;
  }) => send<ProbeResult>('/admin/accounts/probe', { method: 'POST', body: JSON.stringify(input) }),
  accountTest: (id: string) =>
    send<ProbeResult>(`/admin/accounts/${encodeURIComponent(id)}/test`, { method: 'POST' }),

  // Round 29 / v3.5.56: USER-scoped IMAP account management. Same shape as
  // admin/accounts/* but routes through /addin/imap/* — accessible to any
  // authenticated user, filters to accounts they own via user_account_roles.
  myAccountsList: () => send<{ accounts: AccountSummary[] }>('/addin/imap/accounts'),
  myAccountGet: (id: string) => send<AccountSummary>(`/addin/imap/accounts/${encodeURIComponent(id)}`),
  myAccountCreate: (input: {
    label: string;
    timezone?: string;
    imap: { host: string; port?: number; secure?: boolean; username: string; password: string };
  }) =>
    send<AccountSummary>('/addin/imap/accounts', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  myAccountPatch: (
    id: string,
    body: {
      label?: string;
      imap?: {
        host?: string;
        port?: number;
        secure?: boolean;
        username?: string;
        password?: string;
        folders?: AccountSummary['imap']['folders'];
      };
    },
  ) =>
    send<AccountSummary>(`/addin/imap/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  myAccountDelete: (id: string) =>
    send<void>(`/addin/imap/accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  myAccountEnable: (id: string) =>
    send<{ ok: boolean }>(`/addin/imap/accounts/${encodeURIComponent(id)}/enable`, { method: 'POST' }),
  myAccountDisable: (id: string) =>
    send<{ ok: boolean }>(`/addin/imap/accounts/${encodeURIComponent(id)}/disable`, { method: 'POST' }),
  myAccountProbe: (input: {
    host: string;
    port?: number;
    secure?: boolean;
    username: string;
    password: string;
  }) => send<ProbeResult>('/addin/imap/probe', { method: 'POST', body: JSON.stringify(input) }),
  myAccountTest: (id: string) =>
    send<ProbeResult>(`/addin/imap/accounts/${encodeURIComponent(id)}/test`, { method: 'POST' }),

  observabilityCache: () =>
    send<{
      reports: Array<{
        role: string;
        samples: number;
        hits: number;
        hit_rate: number;
        cache_read_token_ratio: number;
        alert: boolean;
      }>;
      shared_cache: Array<{
        kind: string;
        entries: number;
        total_hits: number;
        hot_entries: number;
        hit_per_entry: number;
        oldest: string | null;
        newest: string | null;
      }>;
      template_coverage: {
        classified: number;
        classified_with_template: number;
        coverage_pct: number;
      } | null;
    }>('/admin/observability/cache'),
  observabilityCalibration: () =>
    send<{
      curves: Array<{
        account_id: string;
        tier: string;
        language: string;
        n: number;
        ece: number;
        drift: { alert: boolean; max_bin_drift: number } | null;
      }>;
    }>('/admin/observability/calibration'),

  senderPrefsList: (account_id: string) =>
    send<{ items: SenderPreference[] }>(
      `/admin/accounts/${encodeURIComponent(account_id)}/sender-prefs`,
    ),
  senderPrefUpsert: (account_id: string, sender_email: string, body: SenderPrefUpsert) =>
    send<{ ok: boolean }>(
      `/admin/accounts/${encodeURIComponent(account_id)}/sender-prefs/${encodeURIComponent(sender_email)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  senderPrefDelete: (account_id: string, sender_email: string) =>
    send<{ ok: boolean }>(
      `/admin/accounts/${encodeURIComponent(account_id)}/sender-prefs/${encodeURIComponent(sender_email)}`,
      { method: 'DELETE' },
    ),

  // Round 28-AC Phase B: superadmin addin-release management.
  adminReleaseGet: () =>
    send<{
      physical: {
        latest_version: string;
        sha256: string;
        size_bytes: number;
        published_at: string;
      } | null;
      config: {
        force_below_version: string;
        release_notes_url: string | null;
        updated_at: number | null;
        updated_by: string | null;
      };
      changelogs: Array<{
        version: string;
        bullets: string[];
        notes: string | null;
        published_at: number;
        updated_by: string | null;
      }>;
      telemetry: {
        total_installs: number;
        distribution: Array<{ installed_version: string; users: number }>;
        blocked_installs: number;
        stale_24h: number;
      };
    }>('/admin/release'),
  adminReleasePatch: (body: { force_below_version?: string; release_notes_url?: string | null }) =>
    send<{ ok: boolean; force_below_version: string; release_notes_url: string | null }>(
      '/admin/release',
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  adminReleaseChangelogPut: (version: string, body: { bullets?: string[]; notes?: string | null }) =>
    send<{ ok: boolean; version: string; bullets: string[]; notes: string | null }>(
      `/admin/release/changelog/${encodeURIComponent(version)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  adminReleaseChangelogDelete: (version: string) =>
    send<{ ok: boolean; deleted: number }>(
      `/admin/release/changelog/${encodeURIComponent(version)}`,
      { method: 'DELETE' },
    ),
  adminReleaseInstalls: () =>
    send<{
      installs: Array<{
        user_id: string;
        installed_version: string;
        user_agent: string | null;
        last_seen_at: number;
        first_seen_at: number;
        last_seen_ip: string | null;
        email: string | null;
        display_name: string | null;
        global_role: string | null;
        status: string | null;
      }>;
    }>('/admin/release/installs'),
  adminReleaseInstallDelete: (user_id: string) =>
    send<{ ok: boolean; deleted: number }>(
      `/admin/release/installs/${encodeURIComponent(user_id)}`,
      { method: 'DELETE' },
    ),

  // Round 28-AC: public self-service registration. Two-step.
  authRegisterStart: (input: { email: string; display_name: string; phone_e164?: string }) =>
    send<{ ok: boolean; expires_in_sec: number }>('/auth/register/start', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  authRegisterVerify: (input: { email: string; code: string }) =>
    send<{
      ok: boolean;
      user_id: string;
      email: string;
      display_name: string;
      sign_in_url: string;
      addin_download_url: string;
      email_sent: boolean;
      email_error: string | null;
    }>('/auth/register/verify', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  authLoginBegin: (email: string) =>
    send<{ options: PublicKeyCredentialRequestOptionsJSON; challenge_id: string }>(
      '/auth/login/begin',
      { method: 'POST', body: JSON.stringify({ email }) },
    ),
  authLoginFinish: (challenge_id: string, assertion: unknown) =>
    send<{ access_token: string; user: AuthUser }>('/auth/login/finish', {
      method: 'POST',
      body: JSON.stringify({ challenge_id, assertion }),
    }),
  authMe: () => send<{ sub: string; global_role: string; accounts: Record<string, string> }>('/auth/me'),
  authLogout: () => send<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  // Password + TOTP/SMS auth (Round 12).
  authLoginPassword: (email: string, password: string) =>
    send<{
      challenge_id: string;
      methods: { totp: boolean; sms: boolean };
      // Present only when this is a first-use TOTP situation (admin
      // pre-enrolled, user has never paired their authenticator). The
      // dashboard renders the QR + secret right between password and
      // TOTP-code steps so the user can pair on the spot.
      totp_setup?: { qr_data_url: string; secret: string };
    }>('/auth/login/password', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  // ---- Per-user signal patterns (Action Inbox / Cases-suggest tuning).
  // Each user can edit the keyword lists that drive the legal / finance /
  // operator / etc. promote signals + the demote noise list. Returns the
  // user's current set merged with curated defaults so the editor can
  // show "what you have" alongside "what we recommend".
  addinPatterns: () =>
    send<{
      patterns: { promote: Record<string, string[]>; demote: string[] };
      defaults: { promote: Record<string, string[]>; demote: string[] };
      customized: boolean;
    }>('/addin/patterns'),
  addinPatternsUpdate: (patch: { promote?: Record<string, string[]>; demote?: string[] }) =>
    send<{ ok: boolean }>('/addin/patterns', {
      method: 'POST',
      body: JSON.stringify(patch),
    }),
  addinPatternsReset: () =>
    send<{ ok: boolean }>('/addin/patterns/reset', { method: 'POST' }),

  // Per-user category DEFINITIONS — natural-language descriptions of
  // what each category means for this user's business. Fed into the
  // bulk_classifier prompt so the LLM tags emails semantically (no regex).
  // This replaces the keyword editor — kept the keyword endpoints for
  // backward compatibility with already-classified rows that fall back
  // to regex when categories_json is null.
  addinCategoryDefs: () =>
    send<{
      definitions: Record<string, string>;
      defaults: Record<string, string>;
      customized: boolean;
    }>('/addin/category-defs'),
  addinCategoryDefsUpdate: (definitions: Record<string, string>) =>
    send<{ ok: boolean }>('/addin/category-defs', {
      method: 'POST',
      body: JSON.stringify({ definitions }),
    }),
  addinCategoryDefsReset: () =>
    send<{ ok: boolean }>('/addin/category-defs/reset', { method: 'POST' }),

  authProfile: () =>
    send<{
      email: string;
      display_name: string;
      global_role: string;
      language: string;
      job_roles: string[];
      allowed_roles: string[];
    }>('/auth/profile'),
  authProfileUpdate: (patch: { display_name?: string; job_roles?: string[]; language?: string }) =>
    send<{ ok: boolean }>('/auth/profile', {
      method: 'POST',
      body: JSON.stringify(patch),
    }),

  authLogin2faTotp: async (challenge_id: string, code: string) => {
    const dpop_jkt = await safeJkt();
    return send<{ access_token: string; user: AuthUser }>('/auth/login/2fa/totp', {
      method: 'POST',
      body: JSON.stringify({ challenge_id, code, ...(dpop_jkt ? { dpop_jkt } : {}) }),
    });
  },
  authLogin2faSmsSend: (challenge_id: string) =>
    send<{ ok: boolean; phone_hint: string }>('/auth/login/2fa/sms/send', {
      method: 'POST',
      body: JSON.stringify({ challenge_id }),
    }),
  authLogin2faSmsVerify: async (challenge_id: string, code: string) => {
    const dpop_jkt = await safeJkt();
    return send<{ access_token: string; user: AuthUser }>('/auth/login/2fa/sms/verify', {
      method: 'POST',
      body: JSON.stringify({ challenge_id, code, ...(dpop_jkt ? { dpop_jkt } : {}) }),
    });
  },
  authLoginRecovery: async (email: string, password: string, recovery_code: string) => {
    const dpop_jkt = await safeJkt();
    return send<{ access_token: string; user: AuthUser }>('/auth/login/recovery', {
      method: 'POST',
      body: JSON.stringify({ email, password, recovery_code, ...(dpop_jkt ? { dpop_jkt } : {}) }),
    });
  },
  // Round 36: email-based account recovery (no phone needed). User who
  // can't sign in clicks "Locked out?" → email goes out with a link →
  // they click it → set new password + TOTP gets wiped (re-enrolls next
  // login). Phase 1 (begin) is anti-enum, always returns ok.
  authRecoverBegin: (email: string) =>
    send<{ ok: true }>('/auth/recover/begin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  authRecoverComplete: (email: string, token: string, new_password: string) =>
    send<{ ok: true; recovery_codes: string[]; totp_setup: { qr_data_url: string; secret: string } }>('/auth/recover/complete', {
      method: 'POST',
      body: JSON.stringify({ email, token, new_password }),
    }),

  authPasswordResetBegin: (email: string) =>
    send<{ ok: boolean }>('/auth/password/reset/begin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  authPasswordResetFinish: (email: string, sms_code: string, new_password: string) =>
    send<{ ok: boolean }>('/auth/password/reset/finish', {
      method: 'POST',
      body: JSON.stringify({ email, sms_code, new_password }),
    }),
  authPasswordChange: (current: string, new_password: string) =>
    send<{ ok: boolean }>('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ current, new_password }),
    }),
  authSecurity: () =>
    send<{
      totp: { enrolled: boolean; last_used_at: string | null };
      sms: { enrolled: boolean; phone: string | null };
      passkeys_count: number;
      recovery_codes_remaining: number;
    }>('/auth/security'),
  authTotpEnrollBegin: () =>
    send<{ secret: string; otpauth_qr: string }>('/auth/totp/enroll/begin', { method: 'POST' }),
  authTotpEnrollFinish: (code: string) =>
    send<{ ok: boolean; recovery_codes: string[] }>('/auth/totp/enroll/finish', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  authPhoneBegin: (phone_e164: string) =>
    send<{ ok: boolean; phone_hint: string }>('/auth/phone/begin', {
      method: 'POST',
      body: JSON.stringify({ phone_e164 }),
    }),
  authPhoneFinish: (code: string) =>
    send<{ ok: boolean }>('/auth/phone/finish', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  authSetup: (input: {
    token: string;
    email: string;
    display_name: string;
    password: string;
    phone?: string;
  }) =>
    send<{
      user_id: string;
      totp_secret: string;
      otpauth_qr: string;
      recovery_codes: string[];
      next: string;
    }>('/auth/setup', { method: 'POST', body: JSON.stringify(input) }),
  authSetupFinish: (user_id: string, code: string, token: string) =>
    send<{ ok: boolean; access_token: string; user: AuthUser }>('/auth/setup/finish', {
      method: 'POST',
      body: JSON.stringify({ user_id, code, token }),
    }),

  passkeysList: () =>
    send<{
      items: Array<{
        id: string;
        device_name: string | null;
        created_at: string;
        last_used_at: string | null;
        revoked_at: string | null;
      }>;
    }>('/auth/passkeys'),
  passkeyRegisterBegin: (device_name: string) =>
    send<{ options: PublicKeyCredentialCreationOptionsJSON }>('/auth/passkeys/register/begin', {
      method: 'POST',
      body: JSON.stringify({ device_name }),
    }),
  passkeyRegisterFinish: (challenge: string, attestation: unknown, device_name: string) =>
    send<{ ok: boolean; id: string; device_name: string }>('/auth/passkeys/register/finish', {
      method: 'POST',
      body: JSON.stringify({ challenge, attestation, device_name }),
    }),
  passkeyRevoke: (id: string) =>
    send<{ ok: boolean }>(`/auth/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  settingsGetAll: () => send<{ settings: AllSettings; epoch: number }>('/admin/settings'),
  settingsGet: <C extends keyof AllSettings>(category: C) =>
    send<{ category: C; settings: AllSettings[C]; epoch: number }>(`/admin/settings/${category}`),
  settingsPut: <C extends keyof AllSettings>(category: C, body: AllSettings[C]) =>
    send<{ category: C; settings: AllSettings[C]; epoch: number }>(`/admin/settings/${category}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // Round 26: addin endpoints exposed on the brain — same routes the COM addin uses.
  addinSearch: (q: string, limit = 50) =>
    send<{
      hits: Array<{
        id: string;
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
        snippet: string;
      }>;
    }>(`/addin/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  addinEmail: (message_id: string) =>
    send<{
      message_id: string;
      subject: string | null;
      from_addr: string;
      to_addrs: string[];
      cc_addrs: string[];
      body: string;
      body_unavailable?: boolean;
      received_at: string;
      language: string | null;
      folder: string | null;
      classification_tier: string | null;
      classification_confidence: number | null;
    }>(`/addin/email/${encodeURIComponent(message_id)}`),
  addinTriage: (subject: string, from: string, body: string, language?: string) =>
    send<{ tier: string; confidence: number; language: string | null; reasoning: string }>(
      '/addin/inline/classify',
      { method: 'POST', body: JSON.stringify({ subject, from, body, language }) },
    ),
  addinAnalyze: (
    subject: string,
    from: string,
    body: string,
    language?: string,
    message_id?: string,
  ) =>
    send<{
      summary: string;
      latest_ask: string | null;
      priority: 'critical' | 'high' | 'medium' | 'low';
      urgency: 'immediate' | 'today' | 'this_week' | 'no_deadline';
      business_area: string;
      sender_type: string;
      action_required: boolean;
      action_type: string;
      thread_state: 'waiting_on_me' | 'waiting_on_them' | 'delegated' | 'done' | 'fyi' | 'ambiguous';
      risk_type: string;
      tier: string;
      confidence: number;
      language: string;
      sentiment: 'positive' | 'neutral' | 'negative' | 'urgent' | 'frustrated';
      categories: string[];
      action_items: Array<{
        text: string;
        owner: 'me' | 'sender' | 'other';
        due: string | null;
        priority: 'high' | 'medium' | 'low';
      }>;
      key_entities: string[];
      deadline: string | null;
      reasons: string[];
      security_signals: string[];
      needs_reply: boolean;
      suggested_reply_intent: string;
      _cache?: { hit: boolean; model: string; cost_usd?: number; cached_at?: string };
    }>('/addin/inline/analyze', {
      method: 'POST',
      body: JSON.stringify({ subject, from, body, language, message_id }),
    }),
  addinDraftWithIntent: (input: {
    subject: string;
    from: string;
    body: string;
    intent?: string;
    tone?: string;
    length?: string;
    user_intent?: string;
    constraints?: Record<string, boolean>;
  }) =>
    send<{ draft: string }>('/addin/inline/draft', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  addinSnooze: (message_id: string, until: string, reason?: string) =>
    send<{ ok: boolean }>('/addin/snooze', {
      method: 'POST',
      body: JSON.stringify({ message_id, until, reason }),
    }),
  addinUnsnooze: (message_id: string) =>
    send<{ ok: boolean }>(`/addin/snooze/${encodeURIComponent(message_id)}`, { method: 'DELETE' }),
  addinSnoozedList: () =>
    send<{
      snoozed: Array<{
        message_id: string;
        snoozed_at: string;
        snoozed_until: string;
        reason: string | null;
        subject: string | null;
        from_addr: string | null;
        received_at: string | null;
      }>;
    }>('/addin/snoozed'),
  addinSenderProfile: (email: string) =>
    send<{
      email: string;
      total: number;
      last_contact: { subject: string | null; received_at: string; message_id: string } | null;
      recent_subjects: Array<{
        subject: string | null;
        received_at: string;
        message_id: string;
        classification_tier: string | null;
      }>;
      tier_histogram: Array<{ tier: string; n: number }>;
      is_vip: boolean;
      // Phase 6a deeper profile fields
      sent_by_me_count: number;
      first_contact_at: string | null;
      days_known: number;
      relationship_strength: number;
      volume_trend: Array<{ week_start: string; count: number }>;
      my_avg_reply_hours: number | null;
      their_avg_reply_hours: number | null;
      open_cases_count: number;
      topics_top: Array<{ word: string; count: number }>;
    }>(`/addin/sender/${encodeURIComponent(email)}`),
  addinVipSet: (email: string, is_vip: boolean, label?: string) =>
    send<{ ok: boolean }>('/addin/vip', {
      method: 'POST',
      body: JSON.stringify({ email, is_vip, label }),
    }),
  addinCasesList: () =>
    send<{
      cases: Array<{
        id: string;
        name: string;
        summary: string | null;
        status: string;
        priority: string;
        created_at: string;
        updated_at: string;
        message_count: number;
        latest_activity: string | null;
      }>;
    }>('/addin/cases'),
  addinCaseGet: (id: string) =>
    send<{
      case: {
        id: string;
        name: string;
        summary: string | null;
        status: string;
        priority: string;
        created_at: string;
        updated_at: string;
      };
      messages: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string | null;
        received_at: string | null;
        classification_tier: string | null;
        thread_id: string | null;
        added_at: string;
      }>;
    }>(`/addin/cases/${encodeURIComponent(id)}`),
  addinCaseCreate: (input: {
    name: string;
    summary?: string;
    message_ids?: string[];
    priority?: string;
  }) =>
    send<{ id: string; name: string; status: string }>('/addin/cases', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  addinCasePatch: (
    id: string,
    body: { name?: string; summary?: string; status?: string; priority?: string },
  ) =>
    send<{ ok: boolean }>(`/addin/cases/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  addinCaseAddMessages: (id: string, message_ids: string[]) =>
    send<{ ok: boolean; added: number }>(`/addin/cases/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message_ids }),
    }),
  addinCaseRemoveMessage: (id: string, message_id: string) =>
    send<{ ok: boolean }>(
      `/addin/cases/${encodeURIComponent(id)}/messages/${encodeURIComponent(message_id)}`,
      { method: 'DELETE' },
    ),
  addinCaseDelete: (id: string) =>
    send<{ ok: boolean }>(`/addin/cases/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  addinCasesBatch: (ids: string[], action: 'close' | 'reopen' | 'delete') =>
    send<{ ok: boolean; affected: number; action: string }>(
      '/addin/cases/batch',
      { method: 'POST', body: JSON.stringify({ ids, action }) },
    ),
  addinCasesMembership: () =>
    send<{
      memberships: Array<{
        message_id: string;
        case_id: string;
        case_name: string;
        status: string;
      }>;
    }>('/addin/cases/membership'),
  // Phase 6a — case suggestions
  addinCasesSuggest: (days = 60) =>
    send<{
      generated_at: string;
      window_days: number;
      suggestions: Array<{
        kind: 'long_thread' | 'cluster';
        suggestion_id: string;
        suggested_name: string;
        reason: string;
        thread_ids: string[];
        message_ids: string[];
        participant_count: number;
        msg_count: number;
        first_at: string;
        last_at: string;
        tier_top: string | null;
      }>;
    }>(`/addin/cases/suggest?days=${days}`),
  addinCaseFromSuggestion: (body: {
    name: string;
    summary?: string;
    priority?: string;
    message_ids: string[];
  }) =>
    send<{ ok: boolean; case_id: string; attached: number }>(
      '/addin/cases/from-suggestion',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  // Phase 7 — Risk feed
  addinRiskFeed: (days = 14, limit = 30, offset = 0) =>
    send<{
      generated_at: string;
      window_days: number;
      total_flagged: number;
      offset: number;
      limit: number;
      has_more: boolean;
      items: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        thread_id: string | null;
        score: number;
        verdict: 'clean' | 'suspicious' | 'dangerous';
        signals: Array<{ type: string; severity: 'low' | 'medium' | 'high'; detail: string }>;
        from_cache: boolean;
      }>;
    }>(`/addin/risk-feed?days=${days}&limit=${limit}&offset=${offset}`),
  // Phase 6b — reply brief
  addinReplyBrief: (params: { message_id?: string; email?: string }) => {
    const qs = new URLSearchParams();
    if (params.message_id) qs.set('message_id', params.message_id);
    if (params.email) qs.set('email', params.email);
    return send<{
      generated_at: string;
      target_email: string;
      thread_id: string | null;
      relationship_snapshot: {
        email: string;
        is_vip: boolean;
        total_emails: number;
        last_contact_at: string | null;
        last_contact_subject: string | null;
        days_known: number;
      };
      their_recent_asks: Array<{
        message_id: string; subject: string | null; received_at: string; snippet: string;
      }>;
      your_promises_to_them: Array<{
        id: string; promise_text: string; due_at: string | null;
        source_message_id: string; detected_at: string;
      }>;
      their_promises_to_you: Array<{
        id: string; promise_text: string; due_at: string | null;
        source_message_id: string; detected_at: string;
      }>;
      recent_decisions: Array<{
        id: string; decision_text: string; decided_at: string; message_id: string;
      }>;
      unresolved_threads: Array<{
        message_id: string; subject: string | null; received_at: string; thread_id: string;
      }>;
    }>(`/addin/reply-brief?${qs.toString()}`);
  },
  addinSecurityScan: (message_id: string, force_refresh = false) =>
    send<{
      verdict: 'clean' | 'suspicious' | 'dangerous';
      risk_score: number;
      signals: Array<{ type: string; severity: 'low' | 'medium' | 'high'; detail: string }>;
      scanned_at: string;
      cached: boolean;
      virustotal_enabled?: boolean;
    }>('/addin/security/scan', {
      method: 'POST',
      body: JSON.stringify({ message_id, force_refresh }),
    }),
  addinHealth: () =>
    send<{
      score: number;
      grade: 'A' | 'B' | 'C' | 'D' | 'F';
      breakdown: Record<string, { count: number; penalty: number }>;
      // Phase 9 — Inbox Control Score actionable shape
      buckets: Array<{
        key: string;
        label: string;
        count: number;
        penalty: number;
        action_label: string | null;
        action_kind: string | null;
        potential_recovery: number;
      }>;
      improvement_path: Array<{
        label: string;
        action_kind: string;
        impact: string;
        bucket: string;
      }>;
    }>('/addin/health'),
  // Phase 9 — Morning Brief 2.0
  addinMorningBrief: (tz?: string) =>
    send<{
      generated_at: string;
      greeting: string;
      timezone: string;
      yesterday_recap: {
        sent_by_me: number;
        incoming: number;
        promises_resolved: number;
        rules_or_cases_activated: number;
        undos: number;
      };
      today_top_3: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
        is_vip: number;
      }>;
      risk_alerts: Array<{
        message_id: string;
        verdict: string;
        risk_score: number;
        subject: string | null;
        from_addr: string;
        received_at: string;
      }>;
      decisions_recent: Array<{
        id: string; decision_text: string; decided_at: string; message_id: string;
      }>;
      promises_due_today: Array<{
        id: string; promise_text: string; due_at: string;
        source_message_id: string; audience: string | null;
      }>;
    }>(`/addin/morning-brief${tz ? `?tz=${encodeURIComponent(tz)}` : ''}`),
  addinCleanupSuggestions: () =>
    send<{
      suggestions: Array<{
        kind: string;
        from_addr?: string;
        sample_subject?: string;
        affected_count: number;
        action_label: string;
        action_payload: Record<string, unknown>;
        reason: string;
      }>;
    }>('/addin/cleanup-suggestions'),
  addinChangesSince: (since?: string) =>
    send<{
      since: string;
      new_count: number;
      new_senders: Array<{ from_addr: string; first_seen: string }>;
      critical_new: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
      }>;
      security_hits: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        verdict: string;
        risk_score: number;
      }>;
    }>(`/addin/changes-since${since ? `?since=${encodeURIComponent(since)}` : ''}`),
  addinPromisesScan: (days = 14) =>
    send<{ scanned: number; extracted: number }>('/addin/promises/scan', {
      method: 'POST',
      body: JSON.stringify({ days }),
    }),
  addinPromisesList: (status: 'pending' | 'fulfilled' | 'cancelled' = 'pending') =>
    send<{
      promises: Array<{
        id: string;
        source_message_id: string;
        subject: string | null;
        promise_text: string;
        maker: string;
        audience: string | null;
        due_at: string | null;
        status: string;
        detected_at: string;
      }>;
    }>(`/addin/promises?status=${status}`),
  addinPromisePatch: (id: string, body: {
    status?: 'pending' | 'fulfilled' | 'cancelled';
    due_at?: string | null;
    promise_text?: string;
  }) =>
    send<{ ok: boolean }>(`/addin/promises/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  addinRadar: () =>
    send<{
      generated_at: string;
      new_risks: Array<{ message_id: string; subject: string | null; from_addr: string; received_at: string }>;
      new_relationships: Array<{ thread_id: string; from_addr: string; first_seen_in_thread: string; subject: string | null }>;
      topic_spikes: Array<{ from_addr: string; n_today: number; n_baseline_avg: number; multiplier: number }>;
      silent_customers: Array<{ from_addr: string; last_received: string; total: number }>;
    }>('/addin/radar'),
  addinPreSendCheck: (input: { subject: string; from?: string; to?: string; body: string; has_attachments?: boolean }) =>
    send<{
      ok: boolean;
      can_send: boolean;
      warnings: Array<{ severity: 'high' | 'medium' | 'low'; type: string; detail: string }>;
    }>('/addin/pre-send-check', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  addinSetTier: (message_id: string, tier: string | null) =>
    send<{ ok: boolean; previous: string | null; current: string | null }>(
      `/addin/email/${encodeURIComponent(message_id)}/tier`,
      { method: 'PATCH', body: JSON.stringify({ tier }) },
    ),
  addinCloseout: () =>
    send<{
      generated_at: string;
      waiting_on_you_today: number;
      overdue_promises: number;
      snoozed_returning_soon: number;
    }>('/addin/closeout'),

  // ---- Round 40 ----
  addinImportantNotUrgent: () =>
    send<{
      items: Array<{
        id: string; message_id: string; subject: string | null;
        from_addr: string; received_at: string; classification_tier: string | null;
      }>;
    }>('/addin/important-not-urgent'),
  addinNoiseBudget: () =>
    send<{
      items: Array<{
        from_addr: string; total_30d: number; daily_avg: number;
        suggested_threshold: number; is_machine: boolean;
      }>;
    }>('/addin/noise-budget'),
  addinAntiVipsList: () =>
    send<{ items: Array<{ email: string; added_at: string; reason: string | null }> }>('/addin/anti-vips'),
  addinAntiVipAdd: (email: string, reason?: string) =>
    send<{ ok: boolean }>('/addin/anti-vips', {
      method: 'POST',
      body: JSON.stringify({ email, reason }),
    }),
  addinAntiVipRemove: (email: string) =>
    send<{ ok: boolean }>(`/addin/anti-vips/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  addinSenderDeleteAll: (email: string) =>
    send<{ ok: boolean; deleted: number }>('/addin/sender/delete-all', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  addinSlaViolations: () =>
    send<{
      violations: Array<{
        id: string; message_id: string; subject: string | null;
        from_addr: string; received_at: string; classification_tier: string | null;
        thread_id: string; is_vip: boolean; sla_hours: number;
        age_hours: number; breach_hours: number;
      }>;
    }>('/addin/sla'),
  addinDndGet: () =>
    send<{
      account_id: string;
      quiet_start: string; quiet_end: string; timezone: string;
      weekend_silent: number; vip_breaks_dnd: number; updated_at: string;
    }>('/addin/dnd'),
  addinDndSet: (body: {
    quiet_start?: string; quiet_end?: string; timezone?: string;
    weekend_silent?: boolean; vip_breaks_dnd?: boolean;
  }) =>
    send<{ ok: boolean }>('/addin/dnd', { method: 'POST', body: JSON.stringify(body) }),
  addinResolvedThreads: () =>
    send<{
      items: Array<{
        id: string; message_id: string; subject: string | null;
        from_addr: string; received_at: string; thread_id: string; snippet: string;
      }>;
    }>('/addin/resolved-threads'),
  addinUnsubscribeSuggestions: () =>
    send<{
      items: Array<{ from_addr: string; volume_30d: number; sample_message_id: string }>;
    }>('/addin/unsubscribe-suggestions'),
  addinMorning: () =>
    send<{
      generated_at: string;
      overnight_count: number;
      new_p0: Array<{ id: string; message_id: string; subject: string | null; from_addr: string; received_at: string }>;
      new_security_hits: Array<{ message_id: string; verdict: string; risk_score: number; subject: string | null; from_addr: string }>;
      overdue_promises: number;
      sla_violations: number;
    }>('/addin/morning'),
  addinDecisionsScan: (days = 7) =>
    send<{ scanned: number; extracted: number }>('/addin/decisions/scan', {
      method: 'POST',
      body: JSON.stringify({ days }),
    }),
  addinDecisionsList: () =>
    send<{
      decisions: Array<{
        id: string; decision_text: string; participants_json: string;
        decided_at: string; message_id: string; subject: string | null;
      }>;
    }>('/addin/decisions'),
  addinDecisionPatch: (id: string, body: { decision_text: string }) =>
    send<{ ok: boolean }>(`/addin/decisions/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  addinDecisionDelete: (id: string) =>
    send<{ ok: boolean }>(`/addin/decisions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  addinPromisesBatch: (ids: string[], status: 'pending' | 'fulfilled' | 'cancelled') =>
    send<{ ok: boolean; affected: number; status: string }>('/addin/promises/batch', {
      method: 'POST', body: JSON.stringify({ ids, status }),
    }),
  addinDecisionsBatch: (ids: string[]) =>
    send<{ ok: boolean; affected: number }>('/addin/decisions/batch', {
      method: 'POST', body: JSON.stringify({ ids, action: 'delete' }),
    }),
  addinReplyWithEvidence: (message_id: string, user_intent?: string) =>
    send<{ draft: string; evidence_count: number }>('/addin/reply-with-evidence', {
      method: 'POST',
      body: JSON.stringify({ message_id, user_intent }),
    }),
  addinRecipient: (email: string) =>
    send<{
      email: string;
      stats: {
        total: number; sent_to: number; received_from: number;
        first_contact: string | null; last_contact: string | null;
      };
      is_vip: boolean;
      is_anti_vip: boolean;
      recent: Array<{ message_id: string; subject: string | null; received_at: string; classification_tier: string | null }>;
    }>(`/addin/recipient/${encodeURIComponent(email)}`),
  addinAsk: (question: string) =>
    send<{
      answer: string;
      cited_indices: number[];
      sources: Array<{
        index: number;
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        snippet: string;
        cited: boolean;
      }>;
    }>('/addin/ask', { method: 'POST', body: JSON.stringify({ question }) }),
  // Round 29 / v3.5.55: Organize-with-AI plan endpoint.
  addinOrganizePlan: (prompt: string) =>
    send<
      | {
          applicable: false;
          rejection_reason: string;
        }
      | {
          applicable: true;
          criteria: {
            from_pattern: string | null;
            subject_pattern: string | null;
            since_iso: string | null;
            until_iso: string | null;
            folder_filter: string | null;
          };
          target_folder_path: string;
          human_summary: string;
          matching: {
            count: number;
            capped_at: number;
            sample: Array<{
              id: string;
              message_id: string;
              subject: string | null;
              from_addr: string;
              from_name: string | null;
              received_at: string;
              folder: string | null;
              classification_tier: string | null;
            }>;
            all_message_ids: Array<{
              message_id: string;
              from_addr: string;
              received_at: string;
              subject: string | null;
              folder: string | null;
            }>;
          };
        }
    >('/addin/organize/plan', { method: 'POST', body: JSON.stringify({ prompt }) }),
  addinOrganizeAudit: (body: { plan_summary: string; count: number; target_folder_path: string; succeeded: number; failed: number }) =>
    send<{ ok: true }>('/addin/organize/audit', { method: 'POST', body: JSON.stringify(body) }),
  // Round 29 / v3.5.55: self-service diagnose. Returns markdown the user
  // can copy + paste back. Routed through sendText so DPoP proof, 401
  // refresh-token rotation, and 503/504 retry all apply.
  addinDiagnoseMe: (): Promise<string> => sendText('/addin/diagnose/me'),
  addinThreadTimeline: (thread_id: string) =>
    send<{
      thread_id: string;
      messages: Array<{
        message_id: string; subject: string | null; from_addr: string;
        received_at: string; classification_tier: string | null; snippet: string | null;
      }>;
    }>(`/addin/thread/${encodeURIComponent(thread_id)}/timeline`),
  addinTrainingFeedbackPost: (body: {
    message_id: string; correction_kind: string;
    original_tier?: string; corrected_tier?: string; note?: string;
  }) =>
    send<{ ok: boolean }>('/addin/training-feedback', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addinTrainingFeedbackList: () =>
    send<{
      entries: Array<{
        id: string; message_id: string; original_tier: string | null;
        corrected_tier: string | null; correction_kind: string;
        note: string | null; created_at: string;
        subject: string | null; from_addr: string | null;
      }>;
    }>('/addin/training-feedback'),
  addinRulesFromExamples: (message_ids: string[], user_hint?: string) =>
    send<{
      rule: { name: string; when: Record<string, unknown>; then: Record<string, unknown> };
      examples_used: string[];
    }>('/addin/rules/from-examples', {
      method: 'POST',
      body: JSON.stringify({ message_ids, user_hint }),
    }),
  addinContradictionsScan: (days = 7) =>
    send<{ flagged: number }>('/addin/contradictions/scan', {
      method: 'POST',
      body: JSON.stringify({ days }),
    }),
  addinContradictionsList: () =>
    send<{
      items: Array<{
        id: string;
        claim_a_message_id: string; claim_b_message_id: string;
        claim_a_text: string; claim_b_text: string;
        severity: string; explanation: string;
        reviewed: number; created_at: string;
        subject_a: string | null; subject_b: string | null;
      }>;
    }>('/addin/contradictions'),
  addinContradictionDismiss: (id: string) =>
    send<{ ok: boolean }>(`/addin/contradictions/${encodeURIComponent(id)}/dismiss`, { method: 'PATCH' }),
  addinPersonasList: () =>
    send<{ items: Array<{ id: string; name: string; tone_hint: string; sample_text: string | null; enabled: number; created_at: string }> }>(
      '/addin/personas',
    ),
  addinPersonaCreate: (name: string, tone_hint: string, sample_text?: string) =>
    send<{ id: string; name: string }>('/addin/personas', {
      method: 'POST',
      body: JSON.stringify({ name, tone_hint, sample_text }),
    }),
  addinPersonaDelete: (id: string) =>
    send<{ ok: boolean }>(`/addin/personas/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  addinStatusReport: () =>
    send<{
      period: { since: string; until: string };
      total_received: number;
      total_sent: number;
      tier_breakdown: Array<{ classification_tier: string; n: number }>;
      top_senders: Array<{ from_addr: string; n: number }>;
      decisions_made: number;
      promises_fulfilled: number;
    }>('/addin/status-report'),

  // ---- Round 41 ----
  addinSettingsGet: () =>
    send<{
      account_id: string;
      private_mode: number;
      private_model: string;
      ollama_base_url: string;
      updated_at: string;
      // Phase 8 — Trust Center fields
      excluded_folders: string[];
      routing_overrides: Record<string, 'private' | 'cloud'>;
      email_aliases: string[];
      retention_days_messages: number;
      retention_days_attachments: number;
      retention_days_audit: number;
      retention_days_security: number;
    }>('/addin/settings'),
  addinSettingsSet: (body: {
    private_mode?: boolean;
    private_model?: string;
    ollama_base_url?: string;
    excluded_folders?: string[];
    routing_overrides?: Record<string, 'private' | 'cloud'>;
    retention_days_messages?: number;
    retention_days_attachments?: number;
    retention_days_audit?: number;
    retention_days_security?: number;
  }) =>
    send<{ ok: boolean }>('/addin/settings', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Phase 8 — Audit export + retention
  addinAuditExportUrl: (params: { since?: string; until?: string; format?: 'csv' | 'json' }) => {
    const qs = new URLSearchParams();
    if (params.since) qs.set('since', params.since);
    if (params.until) qs.set('until', params.until);
    if (params.format) qs.set('format', params.format);
    return `/addin/audit/export?${qs.toString()}`;
  },
  addinRetentionPreview: () =>
    send<{
      generated_at: string;
      settings: Record<string, number> | null;
      plan: {
        messages: { cutoff: string | null; would_delete: number };
        attachments: { cutoff: string | null; would_delete: number };
        audit: { cutoff: string | null; would_delete: number };
        security: { cutoff: string | null; would_delete: number };
      };
    }>('/addin/retention/preview'),
  addinRetentionSweep: () =>
    send<{
      ok: boolean;
      deleted: { messages: number; attachments: number; audit: number; security: number };
      cuts: Record<string, string | null>;
    }>('/addin/retention/sweep', { method: 'POST' }),
  addinSettingsTestOllama: (body: { base_url?: string; model?: string }) =>
    send<{
      ok: boolean;
      models?: Array<{ name: string; size_mb: number }>;
      sample?: string;
      latency_ms?: number;
      error?: string;
    }>('/addin/settings/test-ollama', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addinAttachmentVersionsList: () =>
    send<{
      items: Array<{
        filename: string;
        distinct_versions: number;
        total_copies: number;
        first_seen: string;
        last_seen: string;
      }>;
    }>('/addin/attachments/versions'),
  addinAttachmentVersionsFor: (filename: string) =>
    send<{
      filename: string;
      versions: Array<{
        id: string;
        message_id: string;
        content_hash: string;
        size_bytes: number;
        created_at: string;
        version_number: number;
        copies: Array<{
          id: string;
          message_id: string;
          subject: string | null;
          from_addr: string | null;
          received_at: string | null;
          created_at: string;
        }>;
        subject: string | null;
        from_addr: string | null;
        received_at: string | null;
      }>;
    }>(`/addin/attachments/versions/${encodeURIComponent(filename)}`),
  addinMailboxMap: () =>
    send<{
      generated_at: string;
      since: string;
      senders: Array<{ from_addr: string; volume: number; top_tier: string | null; last_seen: string }>;
      domains: Array<{ domain: string; volume: number; senders: number }>;
      edges: Array<{ source: string; target: string; shared_threads: number }>;
    }>('/addin/mailbox-map'),
  addinRuleFromNL: (instruction: string) =>
    send<{
      rule: {
        name: string;
        when: Record<string, unknown>;
        then: Record<string, unknown>;
        schedule: string | null;
      };
      instruction: string;
    }>('/addin/rules/from-natural-language', {
      method: 'POST',
      body: JSON.stringify({ instruction }),
    }),
  addinRuleSimulate: (body: {
    when: Record<string, unknown>;
    then: Record<string, unknown>;
    days?: number;
  }) =>
    send<{
      simulated_at: string;
      window_days: number;
      total_matches: number;
      preview: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
      }>;
      action: Record<string, unknown>;
      summary: string;
    }>('/addin/rules/simulate', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addinScheduledRules: () =>
    send<{
      rules: Array<{
        id: string;
        name: string;
        when_json: string;
        then_json: string;
        schedule: string;
        enabled: number;
        run_count: number;
        last_run_at: string | null;
        created_at: string;
      }>;
    }>('/addin/rules/scheduled'),
  addinRuleRunNow: (rule_id: string) =>
    send<{
      matched_count: number;
      action: Record<string, unknown>;
      sample: Array<{ message_id: string; subject: string | null; from_addr: string }>;
    }>('/addin/rules/run-now', {
      method: 'POST',
      body: JSON.stringify({ rule_id }),
    }),

  // ---- Round 43: email aliases + categories ----
  addinEmailAliasesGet: () =>
    send<{ primary: string; aliases: string[] }>('/addin/email-aliases'),
  addinEmailAliasesSet: (aliases: string[]) =>
    send<{ ok: boolean; count: number }>('/addin/email-aliases', {
      method: 'POST',
      body: JSON.stringify({ aliases }),
    }),

  addinCategoriesList: () =>
    send<{
      categories: Array<{
        id: string; name: string; color: string | null; description: string | null;
        match_senders_json: string; match_domains_json: string;
        match_subject_keywords_json: string; match_body_keywords_json: string;
        auto_apply: number; created_at: string; updated_at: string;
        email_count: number;
      }>;
    }>('/addin/categories'),
  addinCategoryCreate: (body: {
    name: string; color?: string; description?: string;
    match_senders?: string[]; match_domains?: string[];
    match_subject_keywords?: string[]; match_body_keywords?: string[];
    auto_apply?: boolean;
  }) =>
    send<{ id: string; name: string }>('/addin/categories', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addinCategoryPatch: (id: string, body: {
    name?: string; color?: string; description?: string;
    match_senders?: string[]; match_domains?: string[];
    match_subject_keywords?: string[]; match_body_keywords?: string[];
    auto_apply?: boolean;
  }) =>
    send<{ ok: boolean }>(`/addin/categories/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  addinCategoryDelete: (id: string) =>
    send<{ ok: boolean }>(`/addin/categories/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  addinCategoryMessages: (id: string, limit = 50, offset = 0) =>
    send<{
      messages: Array<{
        message_id: string; subject: string | null; from_addr: string;
        received_at: string; classification_tier: string | null;
        added_at: string; auto_applied: number;
      }>;
    }>(`/addin/categories/${encodeURIComponent(id)}/messages?limit=${limit}&offset=${offset}`),
  addinCategoryApply: (id: string, body: {
    message_ids?: string[]; sender_contains?: string;
    subject_contains?: string; body_contains?: string;
    tier?: string; days?: number;
  }) =>
    send<{ ok: boolean; candidates: number; applied: number }>(
      `/addin/categories/${encodeURIComponent(id)}/apply`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  addinCategoryAutoSuggest: () =>
    send<{
      suggestions: Array<{
        suggested_name: string; domain_pattern: string;
        senders: string[]; volume: number; reason: string;
      }>;
    }>('/addin/categories/auto-suggest'),
  addinEmailCategoriesGet: (message_id: string) =>
    send<{ categories: Array<{ id: string; name: string; color: string | null }> }>(
      `/addin/email/${encodeURIComponent(message_id)}/categories`,
    ),
  addinEmailCategoriesSet: (message_id: string, category_ids: string[], replace = false) =>
    send<{ ok: boolean; count: number }>(
      `/addin/email/${encodeURIComponent(message_id)}/categories`,
      { method: 'POST', body: JSON.stringify({ category_ids, replace }) },
    ),

  // ---- Round 44 ----
  addinCategoriesApplyAll: () =>
    send<{
      ok: boolean;
      applied_total: number;
      by_category: Array<{ category_id: string; applied: number }>;
    }>('/addin/categories/apply-all', { method: 'POST' }),
  // ---- PHASE 3 — Safe Automation: Safety Review + Universal Undo ----
  addinRuleSafetyReview: (body: {
    when: Record<string, unknown>;
    then: Record<string, unknown>;
    window_days?: number;
  }) =>
    send<{
      historical_match_count: number;
      sample_matches: Array<{
        message_id: string; subject: string | null; from_addr: string;
        received_at: string; classification_tier: string | null;
      }>;
      risky_terms_found: string[];
      false_positive_risk: 'low' | 'medium' | 'high';
      vip_match_count: number;
      critical_tier_match_count: number;
      replied_to_sender_count: number;
      suggested_exceptions: Array<{ kind: string; value: string; reason: string }>;
      window_days: number;
    }>('/addin/rules/safety-review', { method: 'POST', body: JSON.stringify(body) }),
  addinRuleActivate: (id: string) =>
    send<{ ok: boolean; undo_token: string; undo_until: string }>(
      `/addin/rules/${encodeURIComponent(id)}/activate`,
      { method: 'POST' },
    ),
  addinRuleUndo: (undo_token: string) =>
    send<{ ok: boolean; undone: number }>('/addin/rules/undo', {
      method: 'POST',
      body: JSON.stringify({ undo_token }),
    }),
  addinRuleRecentActivations: () =>
    send<{
      activations: Array<{
        undo_token: string;
        rule_count: number;
        rule_names: string[];
        activated_at: string;
      }>;
    }>('/addin/rules/recent-activations'),

  // ---- PHASE 2 — Index Health + Diagnostics ----
  addinIndexHealth: () =>
    send<{
      total_indexed: number;
      indexed_last_hour: number;
      received_last_24h: number;
      stale_folders: number;
      archive_folders: number;
      total_folders: number;
      total_stores: number;
      archive_stores: number;
      stores: Array<{ store: string; msgs: number; folders: number; latest: string | null; is_archive: boolean }>;
      folders: Array<{ folder: string; msgs: number; latest: string | null; earliest: string | null }>;
    }>('/addin/index-health'),
  // 2026-05-23: poll-able status for the async bulk-index queue. The COM
  // addin POSTs /addin/index/bulk and gets 202 immediately — the brain
  // queues batches for background processing. Without this status feed
  // the dashboard reports "inserted: 1000" the moment the POST returns,
  // hiding the fact that 50 batches may still be queued and pending. UI
  // can poll this while bulk index is active to show real queue depth.
  addinIndexBulkStatus: () =>
    send<{
      paused: boolean;
      global: { processing: number; pending: number; ready_pending: number };
      user: { pending_emails: number; pending_batches: number; processing_batches: number };
    }>('/addin/index/bulk-status'),
  addinDiagnoseEmail: (body: { subject?: string; from?: string; received_after?: string; received_before?: string }) =>
    send<{
      exact_matches: Array<{ message_id: string; subject: string | null; from_addr: string; received_at: string; folder: string }>;
      near_matches: Array<{ message_id: string; subject: string | null; from_addr: string; received_at: string; folder: string }>;
      sender_in_index: { count: number; latest: string | null; folders: string[] } | null;
      reason: string;
      suggestion: string;
    }>('/addin/diagnose-email', { method: 'POST', body: JSON.stringify(body) }),
  // 2026-05-24: newsletter TL;DR feed. Lists recent newsletters with the
  // summary_one_line the classifier wrote. UI groups by sender domain so
  // the user can collapse / mute a domain in one click. PLAN.md §27.
  addinNewsletterFeed: (days = 7, limit = 50) =>
    send<{
      days: number;
      total: number;
      items: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        from_name: string | null;
        received_at: string;
        summary: string | null;
      }>;
      top_senders: Array<{ domain: string; count: number }>;
    }>(`/addin/newsletter-feed?days=${days}&limit=${limit}`),
  // 2026-05-24: per-user quiet hours + vacation mode preferences. Gates
  // the morning brief cron's SMS/email dispatch. PLAN.md §47.
  meGetPreferences: () =>
    send<{
      quiet_hours_start: string | null;
      quiet_hours_end: string | null;
      vacation_mode: boolean;
      vacation_until: string | null;
    }>('/addin/me/preferences'),
  mePutPreferences: (body: {
    quiet_hours_start?: string | null;
    quiet_hours_end?: string | null;
    vacation_mode?: boolean;
    vacation_until?: string | null;
  }) =>
    send<{ ok: true }>('/addin/me/preferences', { method: 'PUT', body: JSON.stringify(body) }),

  // ---- PHASE 1 — Noise Killer ----
  addinNoiseKillerDiagnose: (days = 30) =>
    send<{
      window_days: number;
      total_messages_in_window: number;
      noise_messages_estimated: number;
      noise_pct_estimated: number;
      clusters: Array<{
        cluster_id: string;
        kind: 'sender_volume' | 'subject_template' | 'duplicate' | 'recovery_pair';
        title: string;
        reason: string;
        sender_pattern: string | null;
        subject_pattern: string | null;
        count_in_window: number;
        sample_subjects: string[];
        suggested_action: 'archive' | 'digest' | 'tag' | 'move_to_folder';
        suggested_label: string;
        est_time_saved_min: number;
        risky_terms_in_sample: string[];
        replied_to_count: number;
      }>;
    }>(`/addin/noise-killer/diagnose?days=${days}`),
  addinNoiseKillerPreview: (body: {
    cluster_ids: string[];
    sender_patterns: string[];
    action?: 'archive' | 'tag' | 'digest' | 'move_to_folder';
    exceptions?: { skip_if_subject_contains?: string[]; skip_if_replied?: boolean };
  }) =>
    send<{
      would_affect: number;
      would_skip_due_to_exceptions: number;
      sample_affected: Array<{
        message_id: string; subject: string | null; from_addr: string;
        received_at: string; classification_tier: string | null;
      }>;
      action: string;
    }>('/addin/noise-killer/preview', { method: 'POST', body: JSON.stringify(body) }),
  addinNoiseKillerActivate: (body: {
    sender_patterns: string[];
    action: 'archive' | 'tag' | 'digest' | 'move_to_folder';
    exceptions?: { skip_if_subject_contains?: string[]; skip_if_replied?: boolean };
    label?: string;
  }) =>
    send<{ ok: boolean; rules_created: number; undo_token: string; undo_until: string }>(
      '/addin/noise-killer/activate',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  addinNoiseKillerUndo: (undo_token: string) =>
    send<{ ok: boolean; undone: number }>('/addin/noise-killer/undo', {
      method: 'POST',
      body: JSON.stringify({ undo_token }),
    }),

  addinDuplicatesScan: () =>
    send<{
      groups: Array<{ subj_key: string; from_key: string; day_key: string; dup_count: number }>;
      total_extra_rows: number;
      // Near-duplicates (same subject + sender + day, different body) — informational only.
      near_extra_rows?: number;
    }>('/addin/duplicates/scan'),
  // Round 28-AC: kicks off a background delete job and returns a job_id.
  // Poll addinDuplicatesJob until phase === 'done' | 'error'.
  addinDuplicatesDelete: () =>
    send<{ ok: boolean; job_id: string }>('/addin/duplicates', { method: 'DELETE' }),
  addinDuplicatesJob: (job_id: string) =>
    send<{
      id: string;
      phase: 'scanning' | 'deleting' | 'done' | 'error';
      total: number;
      deleted: number;
      error: string | null;
      elapsed_ms: number;
      finished: boolean;
    }>(`/addin/duplicates/job/${encodeURIComponent(job_id)}`),
  // Round 28-AC: list of Outlook-side targets (entry_id + folder) for the
  // STRICT body-hash dedup grain. Safe to delete from Outlook because each
  // target is a byte-identical copy of a row we're keeping.
  addinDuplicatesOutlookTargets: () =>
    send<{
      targets: Array<{
        message_id: string;
        entry_id: string | null;
        folder: string | null;
        subject: string | null;
        from_addr: string;
        received_at: string;
      }>;
      total: number;
    }>('/addin/duplicates/outlook-targets'),
  addinFolderCounts: () =>
    send<{ folders: Array<{ folder: string; msgs: number; latest: string | null }> }>('/addin/folder-counts'),

  // Round 36 follow-up: count of empty-body messages in the user's account.
  // Drives the "Repair missing bodies" button — shows the remaining backlog
  // so the user knows how much work is left.
  addinIndexEmptyBodies: () =>
    send<{ message_ids: string[]; remaining_total: number }>(
      '/addin/index/empty-bodies?limit=1',
    ),

  addinMorningNarrative: () =>
    send<{
      generated_at: string;
      greeting: string;
      decisions_count: number;
      narrative: string | null;
      active_threads: Array<{
        thread_id: string | null;
        message_id: string;
        subject: string | null;
        from_addr: string;
        latest_received_at: string;
        tier: string | null;
        message_count: number;
        participants: string[];
        ai_draft: string | null;
      }>;
      risks: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        verdict: string;
        score: number;
      }>;
      ai_overnight: {
        classified: number;
        newsletters_handled: number;
        promises_detected: number;
        drafts_generated: number;
        risks_flagged: number;
      };
      team_milestones?: {
        birthdays: Array<{
          email: string;
          full_name: string | null;
          preferred_name: string | null;
          position: string | null;
          department: string | null;
          location: string | null;
          when: 'today' | 'tomorrow' | 'yesterday';
        }>;
        tenure: Array<{
          email: string;
          full_name: string | null;
          preferred_name: string | null;
          position: string | null;
          department: string | null;
          location: string | null;
          years: number;
          anniversary_date: string;
          when: 'today' | 'this_week';
          is_round: boolean;
        }>;
      };
      self_milestone?: {
        kind: 'birthday' | 'tenure';
        years?: number;
        is_round?: boolean;
        display_name: string;
      } | null;
    }>('/addin/morning-narrative'),

  addinPeopleRescanSignatures: () =>
    send<{
      ok: boolean;
      refreshed: number;
      changed: number;
      new_senders: number;
      stale_refreshed: number;
    }>('/addin/people/rescan-signatures', { method: 'POST' }),

  addinPeopleTop: (q?: string) =>
    send<{
      items: Array<{
        from_addr: string;
        volume: number;
        first_seen: string;
        last_seen: string;
        is_vip: boolean;
        domain: string | null;
        title: string | null;
        phone: string | null;
        mobile: string | null;
      }>;
    }>(q ? `/addin/people/top?q=${encodeURIComponent(q)}` : '/addin/people/top'),

  addinSearchScoped: (body: {
    q: string; scope?: 'subject' | 'sender' | 'body' | 'all';
    tier?: string; sender?: string; days?: number; limit?: number;
  }, signal?: AbortSignal) =>
    send<{
      q: string; scope: string; total: number;
      hits: Array<{
        id: string; message_id: string; subject: string | null;
        from_addr: string; received_at: string;
        classification_tier: string | null; snippet: string;
      }>;
    }>('/addin/search/scoped', { method: 'POST', body: JSON.stringify(body), ...(signal !== undefined ? { signal } : {}) }),
  addinAttachmentsForEmail: (message_id: string) =>
    send<{
      attachments: Array<{
        id: string;
        filename: string;
        content_type: string | null;
        size_bytes: number;
        content_hash: string;
        extraction_status: string;
        ai_summary_json: string | null;
        extracted_text_length: number;
        created_at: string;
      }>;
    }>(`/addin/email/${encodeURIComponent(message_id)}/attachments`),
  addinAttachmentExtract: (id: string) =>
    send<{
      document_type: string;
      summary: string;
      parties?: string[];
      amounts?: Array<{ value: string; currency: string; label: string }>;
      dates?: Array<{ value: string; label: string }>;
      key_terms?: string[];
      missing_fields?: string[];
      risk_flags?: string[];
      _cache?: { hit: boolean; model?: string; cost_usd?: number };
    }>(`/addin/attachment/${encodeURIComponent(id)}/extract`, { method: 'POST' }),
  addinRulesList: () =>
    send<{
      rules: Array<{
        id: string;
        name: string;
        when_json: string;
        then_json: string;
        enabled: number;
        times_applied: number;
        created_at: string;
        updated_at: string;
      }>;
    }>('/addin/rules'),
  addinRuleCreate: (input: {
    name: string;
    when: Record<string, unknown>;
    then: Record<string, unknown>;
  }) =>
    send<{ id: string; name: string }>('/addin/rules', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  addinRulePatch: (
    id: string,
    body: {
      name?: string;
      enabled?: boolean;
      when?: Record<string, unknown>;
      then?: Record<string, unknown>;
    },
  ) =>
    send<{ ok: boolean }>(`/addin/rules/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  addinRuleDelete: (id: string) =>
    send<{ ok: boolean }>(`/addin/rules/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  addinRuleSuggestions: () =>
    send<{
      suggestions: Array<{
        id: string;
        reason: string;
        confidence: number;
        when: Record<string, unknown>;
        then: Record<string, unknown>;
        sample_count: number;
      }>;
    }>('/addin/rules/suggestions'),
  addinAuditLog: (limit = 100) =>
    send<{
      entries: Array<{
        id: string;
        action: string;
        actor_user_id: string;
        actor_role_at_time: string;
        target_account_id: string | null;
        ts: string;
        payload_json: string | null;
      }>;
    }>(`/addin/audit?limit=${limit}`),
  addinCommand: (query: string) =>
    send<{ parsed: Record<string, string>; hint: string }>('/addin/command', {
      method: 'POST',
      body: JSON.stringify({ query }),
    }),
  addinDraft: (subject: string, from: string, body: string, user_intent?: string) =>
    send<{ draft: string }>('/addin/inline/draft', {
      method: 'POST',
      body: JSON.stringify({ subject, from, body, user_intent }),
    }),
  addinDigest: () =>
    send<{
      total_indexed: number;
      important_recent: Array<{
        id: string;
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        tier: string | null;
      }>;
      by_tier_counts: Array<{ tier: string; n: number }>;
      duplicates: Array<{
        body_sha256: string;
        count: number;
        message_ids: string[];
        subject_sample: string | null;
      }>;
      top_senders_7d: Array<{ from_addr: string; n: number }>;
    }>('/addin/digest'),
  addinStats: () =>
    send<{
      total_indexed: number;
      today: number;
      last_7d: number;
      last_30d: number;
      critical_7d: number;
      by_tier_30d: Array<{ tier: string; n: number }>;
      by_folder: Array<{ folder: string; n: number; last_received: string | null }>;
      top_senders_7d: Array<{ from_addr: string; n: number; last_received: string }>;
      daily_volume_14d: Array<{ day: string; n: number }>;
    }>('/addin/stats'),
  addinActionInbox: () =>
    send<{
      generated_at: string;
      must_answer_today: Array<{
        id: string;
        message_id: string;
        thread_id: string | null;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
        folder: string | null;
      }>;
      waiting_on_others: Array<{
        id: string;
        message_id: string;
        thread_id: string | null;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
      }>;
      overdue_followups: Array<{
        id: string;
        message_id: string;
        thread_id: string | null;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
      }>;
      business_risks: Array<{
        id: string;
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        risk: string;
      }>;
      infrastructure_incidents: Array<{
        cluster_key: string;
        from: string;
        subject_sample: string;
        count: number;
        first_seen: string;
        last_seen: string;
        message_ids: string[];
        likely_severity: string;
      }>;
      noise_summary: {
        machine_total_7d: number;
        clusters_grouped: number;
        singletons_remaining: number;
      };
    }>('/addin/action-inbox'),
  // Phase 4: 5-state Action Inbox 2.0
  addinActionInbox5State: () =>
    send<{
      generated_at: string;
      counts: { act: number; wait: number; know: number; automate: number; ignore: number };
      items: {
        act: ActionInboxItem[];
        wait: ActionInboxItem[];
        know: ActionInboxItem[];
        automate: ActionInboxItem[];
        ignore: ActionInboxItem[];
      };
    }>('/addin/action-inbox-5state'),
  addinActionInboxTrain: (body: {
    message_id: string;
    shown_in: ActionInboxState;
    says_belongs_in: ActionInboxState;
    note?: string;
  }) =>
    send<{ ok: boolean }>('/addin/action-inbox/train', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addinActionInboxBulk: (body: {
    action: 'snooze_30d' | 'acknowledge' | 'mute_sender' | 'train_to';
    message_ids: string[];
    train_to?: ActionInboxState;
    shown_in?: ActionInboxState;
  }) =>
    send<{ ok: boolean; touched: number; action: string }>('/addin/action-inbox/bulk', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addinFollowups: (days = 3) =>
    send<{
      followups: Array<{
        id: string;
        message_id: string;
        thread_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
        folder: string | null;
      }>;
      days: number;
    }>(`/addin/followups?days=${days}`),

  // Round 28-L Phase 1.2: user-tracked followups CRUD.
  addinUserFollowupsList: (status: 'pending' | 'done' | 'all' = 'pending') =>
    send<{
      followups: Array<{
        id: string; message_id: string | null; thread_id: string | null;
        subject: string | null; from_addr: string | null;
        note: string; due_at: string | null;
        status: string; created_at: string;
        completed_at: string | null; snoozed_until: string | null;
      }>;
    }>(`/addin/user-followups?status=${status}`),
  addinUserFollowupCreate: (input: {
    message_id?: string; thread_id?: string; subject?: string;
    from_addr?: string; note: string; due_at?: string;
  }) =>
    send<{ followup: { id: string; note: string; due_at: string | null; status: string } }>(
      '/addin/user-followups',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  addinUserFollowupPatch: (id: string, fields: { note?: string; due_at?: string | null; status?: string }) =>
    send<{ ok: true }>(`/addin/user-followups/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(fields),
    }),
  addinUserFollowupDelete: (id: string) =>
    send<{ ok: true }>(`/addin/user-followups/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Round 28-L Phase 1.3: reminders CRUD.
  addinRemindersList: (status: 'pending' | 'fired' | 'all' = 'all') =>
    send<{
      reminders: Array<{
        id: string; message_id: string | null; subject: string | null;
        note: string | null; remind_at: string; channel: string; status: string;
        created_at: string; fired_at: string | null; dismissed_at: string | null;
      }>;
    }>(`/addin/reminders?status=${status}`),
  addinReminderCreate: (input: {
    message_id?: string; subject?: string; note?: string;
    remind_at: string; channel?: 'inapp' | 'sms' | 'whatsapp' | 'email';
  }) =>
    send<{ reminder: { id: string; remind_at: string } }>('/addin/reminders', {
      method: 'POST', body: JSON.stringify(input),
    }),
  addinReminderPatch: (id: string, fields: { remind_at?: string; status?: string; note?: string }) =>
    send<{ ok: true }>(`/addin/reminders/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(fields),
    }),
  addinReminderDelete: (id: string) =>
    send<{ ok: true }>(`/addin/reminders/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Round 28-M Phase 2 — AI overlay endpoints.
  addinAskAi: (input: {
    message_id?: string;
    messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  }) =>
    send<{ reply: string; tokens_in: number; tokens_out: number; cost_usd: number; latency_ms: number }>(
      '/addin/ask-ai',
      { method: 'POST', body: JSON.stringify(input) },
    ),

  addinAiInsights: (message_id: string) =>
    send<{
      sender_stats: {
        prior_count: number;
        avg_hours_between: number | null;
        tier_mix: Array<{ tier: string; n: number }>;
      };
      ai_analysis: {
        tone?: string;
        intent?: string;
        suggested_action?: string;
        key_points?: string[];
      };
      current_classification: { tier: string | null; language: string | null };
    }>(`/addin/ai-insights/${encodeURIComponent(message_id)}`, { method: 'POST', body: '{}' }),

  addinBulkAi: (input: { message_ids: string[]; op: 'summarize' | 'draft_replies' | 'extract_actions' }) =>
    send<{
      op: string;
      count: number;
      summary?: string;
      actions?: unknown;
      drafts?: Array<{ message_id: string; subject: string | null; draft: string | null; error?: string }>;
      cost_usd?: number;
    }>('/addin/bulk-ai', { method: 'POST', body: JSON.stringify(input) }),

  addinDraftReply: (input: { message_id: string; tone?: string; instructions?: string }) =>
    send<{ draft: string; tone: string; cost_usd: number; latency_ms: number }>(
      '/addin/draft-reply',
      { method: 'POST', body: JSON.stringify(input) },
    ),

  // Round 28-N Phase 3.2 — sender timeline.
  addinSenderTimeline: (email: string, limit = 200) =>
    send<{
      email: string;
      count: number;
      first_contact_at: string | null;
      last_contact_at: string | null;
      timeline: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        to_addrs: string;
        received_at: string;
        classification_tier: string | null;
        thread_id: string | null;
        folder: string | null;
        snippet: string;
        direction: 'in' | 'out';
        gap_ms: number | null;
      }>;
    }>(`/addin/sender/${encodeURIComponent(email)}/timeline?limit=${limit}`),

  // Round 28-N Phase 3.3 — reply templates CRUD.
  addinReplyTemplatesList: () =>
    send<{
      templates: Array<{
        id: string; name: string; body: string; tone: string | null;
        tags: string[]; use_count: number; last_used_at: string | null;
        created_at: string; updated_at: string;
      }>;
    }>('/addin/reply-templates'),
  addinReplyTemplateCreate: (input: { name: string; body: string; tone?: string; tags?: string[] }) =>
    send<{ id: string }>('/addin/reply-templates', { method: 'POST', body: JSON.stringify(input) }),
  addinReplyTemplatePatch: (id: string, fields: { name?: string; body?: string; tone?: string; tags?: string[]; bump_use?: boolean }) =>
    send<{ ok: true }>(`/addin/reply-templates/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(fields),
    }),
  addinReplyTemplateDelete: (id: string) =>
    send<{ ok: true }>(`/addin/reply-templates/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Round 28-O Phase 4: communication analytics.
  analyticsEngagement: (days = 90) =>
    send<{
      days: number;
      total_senders: number;
      categories: { hot: number; warm: number; cold: number; dormant: number };
      senders: Array<{
        sender_email: string;
        inbound_count: number;
        outbound_count: number;
        last_contact_at: string;
        first_contact_at: string;
        days_since_last_contact: number;
        reply_rate: number;
        priority_share: number;
        score: number;
        category: 'hot' | 'warm' | 'cold' | 'dormant';
      }>;
    }>(`/admin/analytics/engagement?days=${days}`),
  analyticsFunnel: (days = 90) =>
    send<{
      days: number;
      stages: Array<{
        stage: string;
        label: string;
        count: number;
        pct_of_top: number;
        drop_from_prev: number | null;
      }>;
    }>(`/admin/analytics/funnel?days=${days}`),
  // Round 28-P Phase 5: goals + autopilot.
  addinGoalsList: () =>
    send<{
      goals: Array<{
        id: string; name: string; description: string | null;
        target_date: string | null; status: string;
        created_at: string; updated_at: string; completed_at: string | null;
      }>;
    }>('/addin/goals'),
  addinGoalCreate: (input: { name: string; description?: string; target_date?: string }) =>
    send<{ id: string }>('/addin/goals', { method: 'POST', body: JSON.stringify(input) }),
  addinGoalPatch: (id: string, fields: { name?: string; description?: string | null; target_date?: string | null; status?: string }) =>
    send<{ ok: true }>(`/addin/goals/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) }),
  addinGoalDelete: (id: string) =>
    send<{ ok: true }>(`/addin/goals/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  addinGoalScore: (id: string, limit = 20) =>
    send<{
      goal: { id: string; name: string; description: string | null };
      scored: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
        score: number;
        why: string;
      }>;
    }>(`/addin/goals/${encodeURIComponent(id)}/score`, { method: 'POST', body: JSON.stringify({ limit }) }),

  addinAutopilotList: () =>
    send<{
      drafts: Array<{
        id: string; message_id: string; subject: string | null; from_addr: string | null;
        draft_body: string; tone: string | null; confidence: number | null;
        generated_at: string; status: string;
        reviewed_at: string | null; sent_at: string | null;
        edited_body: string | null; cost_usd: number;
      }>;
    }>('/addin/autopilot'),
  addinAutopilotGenerate: (input: { limit?: number; tone?: string }) =>
    send<{
      drafts_generated: number;
      attempted?: number;
      total_cost_usd?: number;
      drafts?: Array<{ id: string; message_id: string }>;
      message?: string;
    }>('/addin/autopilot/generate', { method: 'POST', body: JSON.stringify(input) }),
  addinAutopilotPatch: (id: string, fields: { status?: string; edited_body?: string }) =>
    send<{ ok: true }>(`/addin/autopilot/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) }),
  addinAutopilotDelete: (id: string) =>
    send<{ ok: true }>(`/addin/autopilot/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Round 28-Q: user management.
  usersList: () =>
    send<{
      users: Array<{
        id: string; email: string; display_name: string;
        global_role: string; status: string;
        created_at: string; last_login_at: string | null;
        phone_e164: string | null; phone_verified_at: string | null;
      }>;
    }>('/admin/users'),
  userCreate: (input: { email: string; display_name: string; role?: string; phone_e164?: string }) =>
    send<{
      user_id: string; email: string; display_name: string; role: string;
      temp_password: string; totp_secret: string; otpauth_qr: string;
      recovery_codes: string[]; sign_in_url: string;
      email_sent: boolean; email_error: string | null;
    }>('/admin/users', { method: 'POST', body: JSON.stringify(input) }),
  userPatch: (id: string, fields: { display_name?: string; global_role?: string; status?: string }) =>
    send<{ ok: true }>(`/admin/users/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(fields),
    }),
  userDelete: (id: string) =>
    send<{ ok: true }>(`/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  userResetCreds: (id: string) =>
    send<{
      user_id: string; email: string;
      temp_password: string; totp_secret: string; otpauth_qr: string;
      recovery_codes: string[];
    }>(`/admin/users/${encodeURIComponent(id)}/reset`, { method: 'POST', body: '{}' }),

  analyticsOutreach: (days = 90) =>
    send<{
      days: number;
      subjects: Array<{ subject_prefix: string; sent_count: number; replied_count: number; reply_rate: number }>;
      hours: Array<{ send_hour: number; sent_count: number; replied_count: number; reply_rate: number }>;
      days_of_week: Array<{ dow: number; sent_count: number; replied_count: number; reply_rate: number }>;
    }>(`/admin/analytics/outreach?days=${days}`),

  addinSearchAdvanced: (params: {
    q?: string;
    tier?: string;
    since?: string;
    until?: string;
    sender?: string;
    folder?: string;
    limit?: number;
    sort?: 'date' | 'relevance';
  }, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
    // Round 47f: explicit cache-buster — Caddy was serving stale cached
    // responses from before the sort fix landed. Defeats all caching layers
    // (browser, intermediary, service worker).
    qs.set('_t', String(Date.now()));
    return send<{
      hits: Array<{
        id: string;
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
        folder: string | null;
        snippet: string;
      }>;
    }>(`/addin/search/advanced?${qs.toString()}`, {
      headers: { 'cache-control': 'no-cache' },
      // Round 28-W: AbortSignal so superseded keystroke-driven searches
      // are cancelled at the network layer. Without this, every char typed
      // fires a request and brain processes them all serially even though
      // the user only cares about the latest.
      ...(signal !== undefined ? { signal } : {}),
    });
  },
  // Search clustering — group hits by thread + topic.
  addinSearchCluster: (params: {
    q?: string;
    tier?: string;
    since?: string;
    until?: string;
    sender?: string;
    folder?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
    qs.set('_t', String(Date.now()));
    return send<{
      generated_at: string;
      query: Record<string, string | null>;
      total_hits: number;
      counts: { threads: number; topics: number; singletons: number };
      threads: Array<{
        kind: 'thread';
        group_id: string;
        thread_id: string;
        subject: string;
        participants: string[];
        participant_count: number;
        msg_count: number;
        first_at: string;
        last_at: string;
        tier_top: string | null;
        sample_message_ids: string[];
      }>;
      topics: Array<{
        kind: 'topic';
        group_id: string;
        topic_label: string;
        sender_domain: string;
        msg_count: number;
        first_at: string;
        last_at: string;
        tier_top: string | null;
        sample_message_ids: string[];
        senders: string[];
      }>;
      singletons: Array<{
        id: string;
        message_id: string;
        subject: string | null;
        from_addr: string;
        received_at: string;
        classification_tier: string | null;
        folder: string | null;
        thread_id: string | null;
        snippet: string | null;
      }>;
    }>(`/addin/search/cluster?${qs.toString()}`, {
      headers: { 'cache-control': 'no-cache' },
    });
  },

  // ─── Round 22 dashboard observability ────────────────────────────

  systemOverview: () =>
    send<{
      killswitch_on: boolean;
      ai_actions_enabled: boolean;
      shadow_mode: boolean;
      spend_usd: { hourly: number; today: number; monthly: number; hourly_pct: number; monthly_pct: number };
      caps: { hourly_usd: number; monthly_usd: number };
      messages_total: number;
      classified_24h: number;
      pending_24h: number;
      by_tier_24h: Array<{ tier: string; n: number }>;
      queues: { proposed_rules_pending: number; drafts_review_pending: number };
      instance: { port: string; pid: number; cron_enabled: boolean; imap_idle: boolean };
    }>('/admin/system/overview'),

  spendSummary: (accountId?: string) => {
    const qs = accountId ? `?account_id=${encodeURIComponent(accountId)}` : '';
    return send<{
      filter_account_id: string | null;
      caps: { hourly_usd: number; monthly_usd: number };
      hourly: { spend_usd: number; calls: number; pct: number };
      today: { spend_usd: number; calls: number };
      monthly: { spend_usd: number; calls: number; pct: number };
      by_provider: Array<{ provider: string | null; s: number; n: number }>;
      by_role: Array<{ role: string | null; s: number; n: number; cache_read: number; cache_create: number }>;
      by_account: Array<{ account_id: string | null; s: number; n: number }>;
    }>(`/admin/spend/summary${qs}`);
  },

  spendRecent: (limit = 200, accountId?: string) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (accountId) qs.set('account_id', accountId);
    return send<{
      rows: Array<{
        id: number;
        ts_iso: string;
        cost_usd: number;
        provider: string | null;
        model: string | null;
        role: string | null;
        account_id: string | null;
        cache_creation_tokens: number;
        cache_read_tokens: number;
        input_tokens: number;
        output_tokens: number;
      }>;
    }>(`/admin/spend/recent?${qs.toString()}`);
  },

  proposedRulesList: () =>
    send<{
      rows: Array<{
        id: string;
        account_id: string;
        source: string;
        rule_when_json: string;
        rule_then_json: string;
        evidence_json: string;
        status: string;
        created_at: string;
      }>;
    }>('/admin/proposed-rules'),

  proposedRuleDecide: (id: string, action: 'confirm' | 'reject') =>
    send<{ id: string; status: string; decided_at: string }>(
      `/admin/proposed-rules/${encodeURIComponent(id)}/${action}`,
      { method: 'POST' },
    ),

  minedRules: () =>
    send<{
      rows: Array<{
        rule_id: string;
        first_seen_at: string;
        last_hit_at: string | null;
        hit_count_total: number;
        hit_count_last_window: number;
        window_started_at: string | null;
      }>;
      total: number;
      dead_60d: number;
    }>('/admin/mined-rules'),

  cronState: () =>
    send<{
      enable_cron_this_instance: boolean;
      instance_port: string;
      instance_pid: number;
      locks: Array<{ job_name: string; owner: string; acquired_at: string; expires_at: string }>;
      retries: Array<{ id: string; job_name: string; attempt: number; next_run_at: string; last_error: string | null; created_at: string }>;
    }>('/admin/cron/state'),

  auditLog: (params: { limit?: number; actor?: string; action?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.actor) qs.set('actor', params.actor);
    if (params.action) qs.set('action', params.action);
    return send<{
      rows: Array<{
        id: string;
        seq: number;
        actor_user_id: string | null;
        actor_role_at_time: string | null;
        target_account_id: string | null;
        action: string;
        payload_json: string;
        simulator: number;
        prev_hash: string;
        hash: string;
        created_at: string;
      }>;
      count: number;
    }>(`/admin/audit?${qs.toString()}`);
  },

  goldenTestRecent: () =>
    send<{
      holdout_total: number;
      holdout_by_tier: Array<{ tier: string; n: number }>;
      runs: Array<{
        id: number;
        ts_iso: string;
        total: number;
        fired: number;
        exact: number;
        close: number;
        far: number;
        exact_pct: number;
        alert: number;
        used_holdout: number;
      }>;
    }>('/admin/golden-test/recent'),

  messagesList: (params: {
    account_id?: string;
    tier?: string;
    sender?: string;
    days?: number;
    limit?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.account_id) qs.set('account_id', params.account_id);
    if (params.tier) qs.set('tier', params.tier);
    if (params.sender) qs.set('sender', params.sender);
    if (params.days) qs.set('days', String(params.days));
    if (params.limit) qs.set('limit', String(params.limit));
    return send<{
      rows: Array<{
        id: string;
        account_id: string;
        message_id: string;
        from_addr: string;
        subject: string | null;
        received_at: string;
        classification_tier: string | null;
        classification_confidence: number | null;
        language: string | null;
        email_type: string;
        categories_json: string | null;
        analysis_json: string | null;
      }>;
      count: number;
    }>(`/admin/messages?${qs.toString()}`);
  },

  healthExtended: () =>
    send<{
      counts: Record<string, number>;
      env: Record<string, boolean | number>;
      drift: { ok: boolean; missing: string[] };
      instance: { port: string; pid: number };
    }>('/admin/health/extended'),

  // System health checks (run every 30 min by cron, or manually triggered).
  // Powers the live health panel — color grid + 24h timeline per check.
  healthFull: () =>
    send<{
      latest_run: {
        run_id: string;
        finished_at: string;
        overall: 'ok' | 'warn' | 'fail';
        results: Array<{
          name: string;
          status: 'ok' | 'warn' | 'fail';
          message: string | null;
          duration_ms: number;
          details: Record<string, unknown> | null;
          created_at: string;
        }>;
      } | null;
      timeline_24h: Record<string, Array<{ status: string; created_at: string }>>;
      message?: string;
    }>('/admin/health/full'),

  healthRun: () =>
    send<{
      run_id: string;
      started_at: string;
      finished_at: string;
      overall: 'ok' | 'warn' | 'fail';
      results: Array<{
        name: string;
        status: 'ok' | 'warn' | 'fail';
        message: string;
        duration_ms: number;
        details?: Record<string, unknown>;
      }>;
    }>('/admin/health/run', { method: 'POST', body: JSON.stringify({}) }),

  cronTrigger: (jobName: string) =>
    send<{ ok: boolean; job_name: string; fired_at: string }>(
      `/admin/cron/trigger/${encodeURIComponent(jobName)}`,
      { method: 'POST' },
    ),

  // Round 29: Broadcasts (superadmin → all users) + per-user dismiss.
  broadcastsList: () =>
    send<{ broadcasts: Array<{
      id: string; title: string | null; body: string;
      image_url: string | null; video_url: string | null;
      link_url: string | null; link_text: string | null;
      severity: 'info' | 'warning' | 'critical';
      dismissable: boolean;
      starts_at: string; expires_at: string | null;
      created_by: string; created_at: string; updated_at: string;
    }>; }>('/admin/broadcasts'),
  broadcastCreate: (input: {
    title?: string | null; body: string;
    image_url?: string | null; video_url?: string | null;
    link_url?: string | null; link_text?: string | null;
    severity?: 'info' | 'warning' | 'critical';
    dismissable?: boolean;
    starts_at?: string; expires_at?: string | null;
  }) => send<{ id: string }>('/admin/broadcasts', { method: 'POST', body: JSON.stringify(input) }),
  // PATCH /admin/broadcasts/:id is full-update semantics on the brain side
  // (validateBroadcastBody requires `body` to be a non-empty trimmed
  // string and the SQL writes every column). Caller must send the same
  // shape as broadcastCreate — same shape as the row would have on create.
  broadcastUpdate: (
    id: string,
    input: {
      title?: string | null;
      body: string;
      image_url?: string | null;
      video_url?: string | null;
      link_url?: string | null;
      link_text?: string | null;
      severity?: 'info' | 'warning' | 'critical';
      dismissable?: boolean;
      starts_at?: string;
      expires_at?: string | null;
    },
  ) =>
    send<{ ok: boolean }>(`/admin/broadcasts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  broadcastDelete: (id: string) =>
    send<{ ok: boolean }>(`/admin/broadcasts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  broadcastsActive: () =>
    send<{ broadcasts: Array<{
      id: string; title: string | null; body: string;
      image_url: string | null; video_url: string | null;
      link_url: string | null; link_text: string | null;
      severity: 'info' | 'warning' | 'critical';
      dismissable: boolean;
      starts_at: string; expires_at: string | null;
      created_at: string;
    }>; }>('/addin/broadcasts/active'),
  broadcastDismiss: (id: string) =>
    send<{ ok: boolean }>(`/addin/broadcasts/${encodeURIComponent(id)}/dismiss`, { method: 'POST', body: JSON.stringify({}) }),

  // Round 24
  sendersList: () =>
    send<{
      rows: Array<{
        account_id: string;
        sender_email: string;
        sender_domain: string;
        sender_role: string | null;
        role_confidence: number | null;
        msg_count: number;
        last_subject: string | null;
        last_seen_at: string | null;
        title: string | null;
        phone: string | null;
        mobile: string | null;
      }>;
      count: number;
    }>('/admin/senders'),

  // Round 28-U
  senderDetail: (email: string) =>
    send<{
      sender: {
        account_id: string;
        sender_email: string;
        sender_domain: string;
        sender_role: string | null;
        role_confidence: number | null;
        msg_count: number;
        last_subject: string | null;
        last_seen_at: string | null;
        title: string | null;
        phone: string | null;
        mobile: string | null;
      };
      recent: Array<{
        subject: string;
        received_at: string;
        tier: string | null;
        confidence: number | null;
      }>;
      stats: {
        recent_window_size: number;
        first_contact_in_window: string | null;
        last_seen_at: string | null;
        tier_histogram: Record<string, number>;
        sentiment_histogram: Record<string, number>;
        top_categories: Array<{ category: string; count: number }>;
      };
    }>(`/admin/senders/${encodeURIComponent(email)}`),

  settingsMeta: () =>
    send<{
      rows: Array<{
        category: string;
        updated_at: string;
        age_days: number;
        stale_90d: boolean;
      }>;
    }>('/admin/settings/meta'),

  // Round 25
  spendTimeseries: (days = 7, accountId?: string) => {
    const qs = new URLSearchParams({ days: String(days) });
    if (accountId) qs.set('account_id', accountId);
    return send<{
      days: number;
      bucket_size: string;
      rows: Array<{ bucket: string; s: number; n: number }>;
    }>(`/admin/spend/timeseries?${qs.toString()}`);
  },

  tierTimeseries: (days = 14) =>
    send<{
      days: number;
      bucket_size: string;
      rows: Array<{ day: string; tier: string; n: number }>;
    }>(`/admin/messages/by-tier-timeseries?days=${days}`),

  // Round 28-F: Routing Studio.
  routingState: () =>
    send<{
      routing: { roles: Record<string, RoleRouting | undefined> };
      catalog: ModelCatalogEntry[];
      epoch: number;
    }>('/admin/routing/state'),
  routingPreview: (role: string, proposed: RoleRouting) =>
    send<{
      role: string;
      calls_per_day: number;
      projected_daily_cost_usd: number;
      projected_p95_ms: number | null;
      current_daily_cost_usd: number;
      current_p95_ms: number | null;
      notes: string[];
    }>('/admin/routing/preview', {
      method: 'POST',
      body: JSON.stringify({ role, proposed }),
    }),
  // Round 28-H: corpus testing + backlog reclassifier.
  testOnCorpus: (input: { provider: string; model: string; n?: number; include_classified?: boolean; base_url?: string }) =>
    send<{
      provider: string;
      model: string;
      results: Array<{
        message_id: string;
        subject: string | null;
        from_addr: string;
        current_tier: string | null;
        current_confidence: number | null;
        proposed: { category: string; confidence: number | null; reasoning: string | null } | null;
        latency_ms: number;
        cost_usd: number;
        tokens_in: number;
        tokens_out: number;
        error: string | null;
      }>;
      summary: {
        total_calls: number;
        successful: number;
        failed: number;
        total_cost_usd: number;
        avg_cost_per_call: number;
        avg_latency_ms: number;
        p50_latency_ms: number;
        p95_latency_ms: number;
      };
      note?: string;
    }>('/admin/llm/test-on-corpus', { method: 'POST', body: JSON.stringify(input) }),

  reclassifyBacklog: (input: {
    via?: 'rules_only' | 'rules_then_gemma' | 'rules_then_model';
    model?: { provider: string; model: string };
    limit?: number;
    dry_run?: boolean;
  }) =>
    send<{
      processed: number;
      rules_a5_hit: number;
      rules_a_hit: number;
      model_hit: number;
      still_unresolved: number;
      cost_usd: number;
      dry_run: boolean;
      time_limited?: boolean;
      via?: string;
      run_id?: string;
      sample_classifications?: Array<{
        message_id: string;
        from_addr: string;
        subject: string | null;
        via: string;
        tier: string;
        confidence: number;
        reasoning: string;
      }>;
      note?: string;
    }>('/admin/routing/reclassify-backlog', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  routingPipelineStats: (windowHours = 24) =>
    send<{
      window_hours: number;
      total_messages: number;
      classified: number;
      paths: Array<{
        id: string;
        label: string;
        description: string;
        count: number;
        cost_usd: number;
        order: number;
        tier_breakdown?: Record<string, number>;
      }>;
      path_c_calls: number;
      path_c_avg_cost: number;
      generated_at: string;
    }>(`/admin/routing/pipeline-stats?window_hours=${windowHours}`),
  routingShadowStats: (role: string, windowHours = 24) =>
    send<{
      role: string;
      window_hours: number;
      generated_at: string;
      stats: Array<{
        shadow_provider: string;
        shadow_model: string;
        calls: number;
        agreement_rate: number | null;
        agreed_count: number;
        avg_primary_cost: number;
        avg_shadow_cost: number;
        cost_delta_per_call: number;
        avg_primary_latency_ms: number;
        avg_shadow_latency_ms: number;
        latency_delta_ms: number;
        shadow_errors: number;
        error_rate_pct: number;
        age_hours: number;
        ready_to_promote: boolean;
        samples: Array<{
          ts: string;
          primary: string | null;
          shadow: string | null;
          shadow_error: string | null;
        }>;
      }>;
    }>(
      `/admin/routing/shadow-stats/${encodeURIComponent(role)}?window_hours=${windowHours}`,
    ),
  routingSaveRole: (role: string, proposed: RoleRouting, skipShadow = false) =>
    send<{
      role: string;
      routing: { roles: Record<string, RoleRouting | undefined> };
      epoch: number;
    }>(`/admin/routing/role/${encodeURIComponent(role)}`, {
      method: 'PUT',
      body: JSON.stringify({ proposed, skip_shadow: skipShadow }),
    }),

  // ─────── Command Center (Round 30) ───────
  ccBootstrap: () => send<{
    plans: Array<{ id: string; user_id: string; title: string; color: string; sort_order: number; created_at: string; updated_at: string }>;
    buckets: Array<{ id: string; plan_id: string; name: string; sort_order: number; created_at: string }>;
    cards: Array<CcCard>;
    decisions: Array<CcDecision>;
  }>('/addin/cc/bootstrap'),
  ccToday: () => send<{
    overdue: CcCard[]; due_today: CcCard[]; blocking: CcCard[]; revisit: CcDecision[];
  }>('/addin/cc/today'),
  ccPlanCreate: (input: { title: string; color?: string; buckets?: string[] }) =>
    send<{ id: string }>('/addin/cc/plans', { method: 'POST', body: JSON.stringify(input) }),
  ccPlanUpdate: (id: string, input: { title?: string; color?: string; sort_order?: number }) =>
    send<{ ok: true }>(`/addin/cc/plans/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  ccPlanDelete: (id: string) =>
    send<{ ok: true }>(`/addin/cc/plans/${id}`, { method: 'DELETE' }),
  ccBucketCreate: (input: { plan_id: string; name: string }) =>
    send<{ id: string }>('/addin/cc/buckets', { method: 'POST', body: JSON.stringify(input) }),
  ccBucketUpdate: (id: string, input: { name?: string; sort_order?: number }) =>
    send<{ ok: true }>(`/addin/cc/buckets/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  ccBucketDelete: (id: string) =>
    send<{ ok: true }>(`/addin/cc/buckets/${id}`, { method: 'DELETE' }),
  ccCardCreate: (input: {
    plan_id: string; bucket_id: string; title: string;
    description?: string; status?: string; owner?: string;
    start_date?: string; due_date?: string;
    linked_email_thread_id?: string;
  }) =>
    send<{ id: string }>('/addin/cc/cards', { method: 'POST', body: JSON.stringify(input) }),
  ccCardUpdate: (id: string, input: {
    title?: string; description?: string | null; status?: string;
    owner?: string | null;
    start_date?: string | null; due_date?: string | null;
    bucket_id?: string; sort_order?: number; completed?: boolean;
    linked_email_thread_id?: string | null;
  }) =>
    send<{ ok: true }>(`/addin/cc/cards/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  ccCardDelete: (id: string) =>
    send<{ ok: true }>(`/addin/cc/cards/${id}`, { method: 'DELETE' }),
  ccDecisionCreate: (input: {
    title: string; description?: string; options?: string[]; revisit_date?: string;
  }) =>
    send<{ id: string }>('/addin/cc/decisions', { method: 'POST', body: JSON.stringify(input) }),
  ccDecisionUpdate: (id: string, input: {
    title?: string; description?: string | null;
    options?: string[]; decision_made?: string | null;
    revisit_date?: string | null; status?: 'pending' | 'decided' | 'cancelled';
  }) =>
    send<{ ok: true }>(`/addin/cc/decisions/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  ccDecisionDelete: (id: string) =>
    send<{ ok: true }>(`/addin/cc/decisions/${id}`, { method: 'DELETE' }),

  // ─────── Command Center AI helpers (opt-in per action) ───────
  ccAiParseQuickAdd: (input: string) => send<{
    title: string; owner: string | null; status: string;
    start_date: string | null; due_date: string | null; ai_used: boolean;
  }>('/addin/cc/ai/parse-quick-add', { method: 'POST', body: JSON.stringify({ input }) }),
  ccAiSuggestDecisionOptions: (title: string, description?: string) => send<{
    options: string[]; revisit_days: number; revisit_date: string; ai_used: boolean;
  }>('/addin/cc/ai/suggest-decision-options', {
    method: 'POST', body: JSON.stringify({ title, description }),
  }),
  ccAiSuggestPlanBuckets: (title: string) => send<{
    buckets: string[]; ai_used: boolean;
  }>('/addin/cc/ai/suggest-plan-buckets', {
    method: 'POST', body: JSON.stringify({ title }),
  }),
};

export interface CcCard {
  id: string; user_id: string; plan_id: string; bucket_id: string;
  title: string; description: string | null;
  status: string; owner: string | null;
  start_date: string | null; due_date: string | null;
  completed: boolean; completed_at: string | null;
  sort_order: number; linked_email_thread_id: string | null;
  created_at: string; updated_at: string;
}
export interface CcDecision {
  id: string; user_id: string; title: string; description: string | null;
  options: string[]; decision_made: string | null;
  revisit_date: string | null; status: string;
  decided_at: string | null; created_at: string; updated_at: string;
}

export interface ModelRef {
  provider: string;
  model: string;
}

export interface ShadowAssignment {
  model: ModelRef;
  percent: number;
  added_at: string;
}

export interface PrimaryAssignment {
  model: ModelRef;
  percent: number;
}

export interface RoleRouting {
  use_cascade: boolean;
  /** Round 28-G: weighted primary distribution (must sum to 100). */
  primary_split: PrimaryAssignment[];
  /** @deprecated kept for read of legacy saved configs. */
  primary?: ModelRef;
  fallbacks: ModelRef[];
  cascade_tiers?: { haiku: ModelRef; sonnet: ModelRef; opus: ModelRef };
  shadow_models: ShadowAssignment[];
}

export interface ModelCatalogEntry {
  provider: string;
  model: string;
  label: string;
  available: boolean;
  notes: string | null;
  hourly_volume: number[];
  stats: {
    calls_7d: number;
    avg_cost_usd: number;
    total_cost_7d_usd: number;
    avg_input_tokens: number;
    avg_output_tokens: number;
    cache_read_pct: number;
  } | null;
}

// Mirror of brain/src/settings/types.ts. Keep in sync; safe because the API
// returns secrets as `***` masks rather than ciphertext.

export interface ProviderConfig {
  enabled: boolean;
  api_key: string;
  base_url: string;
}

export interface ProvidersSettings {
  anthropic: ProviderConfig;
  openai: ProviderConfig;
  voyage: ProviderConfig;
  litellm_proxy: ProviderConfig;
  gemini: ProviderConfig;
}

export interface BroadNetSettings {
  whatsapp: {
    enabled: boolean;
    api_url: string;
    api_key: string;
    default_template: string;
  };
  sms: {
    enabled: boolean;
    api_url: string;
    username: string;
    password: string;
    sender_id: string;
  };
  smtp?: {
    enabled: boolean;
    host: string;
    port: number;
    secure: boolean;
    username: string;
    password: string;
    from_address: string;
  };
}

export interface BackupSettings {
  enabled: boolean;
  b2_bucket: string;
  b2_endpoint: string;
  b2_key_id: string;
  b2_application_key: string;
  restic_password: string;
}

export interface CascadeSettings {
  threshold_haiku_per_lang: Record<string, number>;
  threshold_sonnet_per_lang: Record<string, number>;
  risk_keyword_hit_forces_opus: boolean;
}

export interface RiskSettings {
  keywords: string[];
  senders: string[];
  domains: string[];
  per_account: Record<string, { keywords?: string[]; senders?: string[]; domains?: string[] }>;
}

export interface RetentionSettings {
  messages_days: number;
  audit_log_days: number;
  classification_audit_days: number;
  vec_documents_days: number;
  drafts_review_days: number;
  cron_retries_days: number;
  // Round 39: server-side IMAP purge. null / undefined / <=0 disables.
  imap_purge_older_than_days?: number | null;
  imap_purge_dry_run?: boolean;
  imap_purge_folders?: string[];
}

export interface AllSettings {
  providers: ProvidersSettings;
  broadnet: BroadNetSettings;
  backup: BackupSettings;
  cascade: CascadeSettings;
  risk: RiskSettings;
  retention: RetentionSettings;
}
